import { useState } from 'react';
import { api } from '../lib/api';

interface Endpoint {
  id: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  title: string;
  description: string;
  auth: string;
  requestBody?: any;
  pathParams?: Array<{ name: string; type: string; required: boolean; description: string }>;
  queryParams?: Array<{ name: string; type: string; required: boolean; description: string; default?: string }>;
  responseExample: any;
  curlExample: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  endpoints: Endpoint[];
}

const METHOD_COLORS = {
  GET: 'bg-blue-500',
  POST: 'bg-green-500',
  PUT: 'bg-yellow-500',
  DELETE: 'bg-red-500',
};

// Define all Points System API endpoints organized by category
const POINTS_API_CATEGORIES: Category[] = [
  {
    id: 'earning',
    name: 'Earning APIs',
    icon: '💰',
    endpoints: [
      {
        id: 'earn-points',
        method: 'POST',
        path: '/api/v1/points/earn',
        title: 'Earn Points',
        description: 'Award points to a user based on invoice amount. Calculates tier-based points, optionally applies welcome bonus (if requested and user is eligible), and enforces limits. Welcome bonus is only given once per user - system tracks eligibility automatically.',
        auth: 'internal',
        requestBody: { 
          userId: 'user_123', 
          invoiceAmount: 50000, 
          invoiceId: 'INV-001', 
          includeWelcomeBonus: true,
          metadata: { source: 'app' } 
        },
        responseExample: { 
          success: true, 
          data: { 
            transactionId: 'txn_xxx',
            pointsEarned: 2500, 
            welcomeBonus: 10000, 
            welcomeBonusRequested: true,
            welcomeBonusApplied: true,
            totalCredited: 12500, 
            newBalance: 25000, 
            tierApplied: { id: 'tier_1', name: 'Tier 1', pointsPerThousand: 50 },
            bonusMultiplier: null,
            message: 'Earned 2,500 points + 10,000 welcome bonus'
          } 
        },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/earn \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_INTERNAL_KEY" \\
  -d '{"userId": "user_123", "invoiceAmount": 50000, "includeWelcomeBonus": true}'`,
      },
      {
        id: 'earn-preview',
        method: 'POST',
        path: '/api/v1/points/earn/preview',
        title: 'Preview Earning',
        description: 'Calculate potential points without actually awarding them. Use to show users how many points they could earn. Pass includeWelcomeBonus: true to check if welcome bonus would be included.',
        auth: 'mobile | internal',
        requestBody: { invoiceAmount: 50000, userId: 'user_123', includeWelcomeBonus: true },
        responseExample: { 
          success: true, 
          data: { 
            invoiceAmount: 50000, 
            basePoints: 2500, 
            tierBreakdown: [{ tierName: 'Tier 1', amountInTier: 50000, pointsPerThousand: 50, pointsEarned: 2500 }], 
            welcomeBonus: 10000, 
            welcomeBonusRequested: true,
            welcomeBonusEligible: true,
            bonusMultiplier: 1, 
            totalPoints: 12500,
            message: 'You could earn up to 12,500 points'
          } 
        },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/earn/preview \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"invoiceAmount": 50000, "userId": "user_123", "includeWelcomeBonus": true}'`,
      },
    ],
  },
  {
    id: 'balance',
    name: 'Balance & Transactions',
    icon: '💳',
    endpoints: [
      {
        id: 'get-balance',
        method: 'GET',
        path: '/api/v1/points/balance/:userId',
        title: 'Get User Balance',
        description: 'Get current points balance for a user including available, locked, and expiring points.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        responseExample: { success: true, data: { userId: 'user_123', totalPoints: 25000, availablePoints: 20000, lockedPoints: 5000, expiringIn30Days: 2000 } },
        curlExample: `curl http://localhost:3000/api/v1/points/balance/user_123 -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'get-balance-detailed',
        method: 'GET',
        path: '/api/v1/points/balance/:userId/detailed',
        title: 'Get Detailed Balance',
        description: 'Get detailed balance including buckets info (for admin/internal use).',
        auth: 'internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        responseExample: { success: true, data: { userId: 'user_123', totalPoints: 25000, availablePoints: 20000, lifetimeStats: { totalEarned: 50000, totalRedeemed: 25000 } } },
        curlExample: `curl http://localhost:3000/api/v1/points/balance/user_123/detailed -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'get-buckets',
        method: 'GET',
        path: '/api/v1/points/balance/:userId/buckets',
        title: 'Get Points Buckets',
        description: 'Get all points buckets for a user showing expiry dates.',
        auth: 'internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        responseExample: { success: true, data: { userId: 'user_123', bucketCount: 3, buckets: [{ id: 'b_xxx', type: 'earned', points: 5000, expiresAt: '2027-01-17' }] } },
        curlExample: `curl http://localhost:3000/api/v1/points/balance/user_123/buckets -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'get-transactions',
        method: 'GET',
        path: '/api/v1/points/transactions/:userId',
        title: 'Get Transaction History',
        description: 'Get paginated transaction history with optional filters by type and date.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page number', default: '1' },
          { name: 'limit', type: 'number', required: false, description: 'Items per page (max 100)', default: '20' },
          { name: 'type', type: 'string', required: false, description: 'EARN, REDEEM, EXPIRE, ADJUST, MIGRATE' },
          { name: 'fromDate', type: 'string', required: false, description: 'ISO 8601 date' },
          { name: 'toDate', type: 'string', required: false, description: 'ISO 8601 date' },
        ],
        responseExample: { success: true, data: { transactions: [{ id: 'txn_xxx', type: 'EARN', points: 2500, balanceAfter: 25000, createdAt: '2026-01-17T10:00:00Z' }], page: 1, hasMore: true } },
        curlExample: `curl "http://localhost:3000/api/v1/points/transactions/user_123?type=EARN&limit=20" -H "x-api-key: YOUR_API_KEY"`,
      },
    ],
  },
  {
    id: 'redemption',
    name: 'Redemption APIs',
    icon: '🎁',
    endpoints: [
      {
        id: 'redemption-preview',
        method: 'POST',
        path: '/api/v1/points/redemption/preview',
        title: 'Preview Redemption',
        description: 'Calculate points/cash split without initiating.',
        auth: 'mobile | internal',
        requestBody: { userId: 'user_123', categoryId: 'mobile_prepaid', amount: 500, pointsToUse: 25000 },
        responseExample: { success: true, data: { amount: 500, pointsToUse: 25000, pointsValue: 250, cashAmount: 250 } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/preview -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"userId":"user_123","categoryId":"mobile_prepaid","amount":500,"pointsToUse":25000}'`,
      },
      {
        id: 'redemption-initiate',
        method: 'POST',
        path: '/api/v1/points/redemption/initiate',
        title: 'Initiate Redemption',
        description: 'Start order, lock points until confirmed/cancelled.',
        auth: 'mobile | internal',
        requestBody: { userId: 'user_123', categoryId: 'mobile_prepaid', amount: 500, pointsToUse: 25000, serviceDetails: { type: 'mobile_recharge', beneficiary: '9876543210' } },
        responseExample: { success: true, data: { orderId: 'order_xxx', status: 'pending_payment', pointsUsed: 25000, cashAmount: 250, paymentRequired: true } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/initiate -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"userId":"user_123","categoryId":"mobile_prepaid","amount":500,"pointsToUse":25000,"serviceDetails":{"type":"mobile_recharge","beneficiary":"9876543210"}}'`,
      },
      {
        id: 'redemption-confirm',
        method: 'POST',
        path: '/api/v1/points/redemption/:orderId/confirm',
        title: 'Confirm Order',
        description: 'Confirm order after payment.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { gateway: 'razorpay', paymentId: 'pay_xxx' },
        responseExample: { success: true, data: { orderId: 'order_xxx', status: 'processing', fulfillmentStatus: 'processing' } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/order_xxx/confirm -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"gateway":"razorpay","paymentId":"pay_xxx"}'`,
      },
      {
        id: 'redemption-cancel',
        method: 'POST',
        path: '/api/v1/points/redemption/:orderId/cancel',
        title: 'Cancel Order',
        description: 'Cancel pending order, release locked points.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { reason: 'User requested cancellation' },
        responseExample: { success: true, data: { orderId: 'order_xxx', status: 'cancelled', pointsReleased: 25000 } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/order_xxx/cancel -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"reason":"Cancelled"}'`,
      },
      {
        id: 'redemption-get-order',
        method: 'GET',
        path: '/api/v1/points/redemption/:orderId',
        title: 'Get Order Status',
        description: 'Get current order status and details.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        responseExample: { success: true, data: { id: 'order_xxx', status: 'completed', pointsUsed: 25000, cashAmount: 250, fulfillment: { status: 'completed' } } },
        curlExample: `curl http://localhost:3000/api/v1/points/redemption/order_xxx -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'redemption-user-orders',
        method: 'GET',
        path: '/api/v1/points/redemption/user/:userId',
        title: 'Get User Orders',
        description: 'Get all orders for a user.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page', default: '1' },
          { name: 'limit', type: 'number', required: false, description: 'Limit', default: '20' },
          { name: 'status', type: 'string', required: false, description: 'Filter by status' },
        ],
        responseExample: { success: true, data: { orders: [{ id: 'order_xxx', amount: 500, status: 'completed' }], hasMore: false } },
        curlExample: `curl "http://localhost:3000/api/v1/points/redemption/user/user_123?limit=20" -H "x-api-key: YOUR_API_KEY"`,
      },
    ],
  },
  {
    id: 'mixed-redemption',
    name: 'Mixed Redemption (Phase 16)',
    icon: '💳',
    endpoints: [
      {
        id: 'mixed-preview',
        method: 'POST',
        path: '/api/v1/points/redemption/preview-mixed',
        title: 'Preview Mixed Redemption',
        description: 'Calculate points/cash split for mixed redemption. Returns how much can be paid with points and how much cash is required.',
        auth: 'mobile | internal',
        requestBody: { 
          userId: 'user_123', 
          categoryId: 'electricity', 
          amount: 500, 
          pointsToUse: 300 
        },
        responseExample: { 
          success: true, 
          data: { 
            amount: 500, 
            maxPointsAllowed: 300, 
            minCashRequired: 200, 
            pointsToUse: 300, 
            pointsValue: 300, 
            cashAmount: 200, 
            userAvailablePoints: 5000, 
            isValid: true, 
            validationErrors: [] 
          } 
        },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/preview-mixed \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"userId": "user_123", "categoryId": "electricity", "amount": 500, "pointsToUse": 300}'`,
      },
      {
        id: 'mixed-initiate',
        method: 'POST',
        path: '/api/v1/points/redemption/initiate-mixed',
        title: 'Initiate Mixed Redemption',
        description: 'Start mixed redemption flow. Locks points and creates Razorpay order for cash portion. Returns Razorpay order details for frontend checkout.',
        auth: 'mobile | internal',
        requestBody: { 
          userId: 'user_123', 
          categoryId: 'electricity', 
          operatorId: 'tata_power', 
          amount: 500, 
          pointsToUse: 300, 
          serviceDetails: { type: 'electricity', beneficiary: '1234567890', operator: 'TATA Power' } 
        },
        responseExample: { 
          success: true, 
          data: { 
            orderId: 'MIX-1705587600000-abc123', 
            razorpayOrderId: 'order_abc123xyz', 
            razorpayKeyId: 'rzp_test_xxx', 
            amountInPaise: 20000, 
            cashAmount: 200, 
            pointsUsed: 300, 
            pointsValue: 300, 
            paymentTimeoutAt: '2026-01-18T12:30:00.000Z',
            order: { id: 'MIX-1705587600000-abc123', status: 'payment_pending', amount: 500 } 
          } 
        },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/initiate-mixed \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"userId": "user_123", "categoryId": "electricity", "operatorId": "tata_power", "amount": 500, "pointsToUse": 300, "serviceDetails": {"type": "electricity", "beneficiary": "1234567890"}}'`,
      },
      {
        id: 'mixed-confirm-payment',
        method: 'POST',
        path: '/api/v1/points/redemption/mixed-order/:orderId/confirm-payment',
        title: 'Confirm Payment',
        description: 'Confirm Razorpay payment after successful checkout. Verifies signature and triggers BBPS payment.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'LinePe order ID' }],
        requestBody: { 
          razorpay_order_id: 'order_abc123xyz', 
          razorpay_payment_id: 'pay_xyz789', 
          razorpay_signature: 'signature_hash' 
        },
        responseExample: { 
          success: true, 
          message: 'Payment confirmed, order is being processed', 
          data: { orderId: 'MIX-1705587600000-abc123', status: 'processing' } 
        },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/mixed-order/MIX-xxx/confirm-payment \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"razorpay_order_id": "order_abc", "razorpay_payment_id": "pay_xyz", "razorpay_signature": "sig"}'`,
      },
      {
        id: 'mixed-order-status',
        method: 'GET',
        path: '/api/v1/points/redemption/mixed-order/:orderId/status',
        title: 'Get Order Status',
        description: 'Check mixed redemption order status with payment and fulfillment details.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        queryParams: [{ name: 'userId', type: 'string', required: false, description: 'User ID for ownership verification' }],
        responseExample: { 
          success: true, 
          data: { 
            orderId: 'MIX-xxx', 
            status: 'completed', 
            amount: 500, 
            pointsUsed: 300, 
            cashAmount: 200, 
            isMixedRedemption: true,
            mixedPayment: { paymentStatus: 'payment_captured', cashAmount: 200 },
            fulfillment: { status: 'completed', completedAt: '2026-01-18T12:35:00Z' }
          } 
        },
        curlExample: `curl "http://localhost:3000/api/v1/points/redemption/mixed-order/MIX-xxx/status?userId=user_123" -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'mixed-order-cancel',
        method: 'POST',
        path: '/api/v1/points/redemption/mixed-order/:orderId/cancel',
        title: 'Cancel Order',
        description: 'Cancel a payment_pending order. Releases locked points. Cannot cancel after payment.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { userId: 'user_123' },
        responseExample: { success: true, message: 'Order cancelled successfully' },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/redemption/mixed-order/MIX-xxx/cancel \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -d '{"userId": "user_123"}'`,
      },
      {
        id: 'mixed-user-orders',
        method: 'GET',
        path: '/api/v1/points/redemption/mixed-orders/:userId',
        title: 'Get User Mixed Orders',
        description: 'Get all mixed redemption orders for a user.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        queryParams: [{ name: 'limit', type: 'number', required: false, description: 'Max results', default: '20' }],
        responseExample: { 
          success: true, 
          data: { 
            orders: [
              { orderId: 'MIX-xxx', status: 'completed', amount: 500, pointsUsed: 300, cashAmount: 200, categoryName: 'Electricity', createdAt: '2026-01-18T12:15:00Z' }
            ], 
            count: 1 
          } 
        },
        curlExample: `curl "http://localhost:3000/api/v1/points/redemption/mixed-orders/user_123?limit=20" -H "x-api-key: YOUR_API_KEY"`,
      },
    ],
  },
  {
    id: 'catalog',
    name: 'Catalog APIs',
    icon: '📦',
    endpoints: [
      {
        id: 'catalog-overview',
        method: 'GET',
        path: '/api/v1/points/catalog',
        title: 'Catalog Overview',
        description: 'Get catalog overview with featured categories and operators.',
        auth: 'mobile | internal',
        responseExample: { success: true, data: { featuredCategories: [], featuredOperators: [] } },
        curlExample: `curl http://localhost:3000/api/v1/points/catalog -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'catalog-categories',
        method: 'GET',
        path: '/api/v1/points/catalog/categories',
        title: 'Get All Categories',
        description: 'Get all active redemption categories.',
        auth: 'mobile | internal',
        responseExample: { success: true, data: { categories: [{ id: 'mobile_prepaid', displayName: 'Mobile Prepaid' }] } },
        curlExample: `curl http://localhost:3000/api/v1/points/catalog/categories -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'catalog-categories-featured',
        method: 'GET',
        path: '/api/v1/points/catalog/categories/featured',
        title: 'Get Featured Categories',
        description: 'Get featured categories for home screen.',
        auth: 'mobile | internal',
        responseExample: { success: true, data: { categories: [{ id: 'mobile_prepaid', displayName: 'Mobile Prepaid', featured: true }] } },
        curlExample: `curl http://localhost:3000/api/v1/points/catalog/categories/featured -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'catalog-operators-by-cat',
        method: 'GET',
        path: '/api/v1/points/catalog/categories/:id/operators',
        title: 'Get Operators by Category',
        description: 'Get operators for a specific category.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Category ID' }],
        responseExample: { success: true, data: { operators: [{ id: 'op_jio', displayName: 'Jio' }] } },
        curlExample: `curl http://localhost:3000/api/v1/points/catalog/categories/mobile_prepaid/operators -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'catalog-operator-details',
        method: 'GET',
        path: '/api/v1/points/catalog/operators/:id',
        title: 'Get Operator Details',
        description: 'Get detailed info about an operator.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Operator ID' }],
        responseExample: { success: true, data: { id: 'op_jio', displayName: 'Jio', logo: 'https://...', planFetchType: 'static' } },
        curlExample: `curl http://localhost:3000/api/v1/points/catalog/operators/op_jio -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'catalog-operator-plans',
        method: 'GET',
        path: '/api/v1/points/catalog/operators/:id/plans',
        title: 'Get Operator Plans',
        description: 'Get recharge plans for an operator.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Operator ID' }],
        queryParams: [
          { name: 'circle', type: 'string', required: false, description: 'Circle code' },
          { name: 'planType', type: 'string', required: false, description: 'Plan type filter' },
        ],
        responseExample: { success: true, data: { plans: [{ id: 'plan_xxx', amount: 199, validity: '28 days' }] } },
        curlExample: `curl "http://localhost:3000/api/v1/points/catalog/operators/op_jio/plans?circle=delhi" -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'catalog-search',
        method: 'GET',
        path: '/api/v1/points/catalog/search',
        title: 'Search Operators',
        description: 'Search operators by name.',
        auth: 'mobile | internal',
        queryParams: [
          { name: 'q', type: 'string', required: true, description: 'Search term (min 2 chars)' },
          { name: 'limit', type: 'number', required: false, description: 'Max results', default: '20' },
        ],
        responseExample: { success: true, data: { operators: [{ id: 'op_jio', displayName: 'Jio' }] } },
        curlExample: `curl "http://localhost:3000/api/v1/points/catalog/search?q=jio" -H "x-api-key: YOUR_API_KEY"`,
      },
    ],
  },
  {
    id: 'payment',
    name: 'Payment APIs',
    icon: '💳',
    endpoints: [
      {
        id: 'payment-initiate',
        method: 'POST',
        path: '/api/v1/points/payment/initiate',
        title: 'Initiate Payment',
        description: 'Create payment order for cash component.',
        auth: 'mobile | internal',
        requestBody: { orderId: 'order_xxx', userId: 'user_123', amount: 250, currency: 'INR' },
        responseExample: { success: true, data: { paymentOrderId: 'pay_xxx', gateway: 'razorpay', gatewayOrderId: 'order_yyy', amount: 250 } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/payment/initiate -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"orderId":"order_xxx","userId":"user_123","amount":250}'`,
      },
      {
        id: 'payment-verify',
        method: 'POST',
        path: '/api/v1/points/payment/verify',
        title: 'Verify Payment',
        description: 'Verify payment after gateway callback.',
        auth: 'mobile | internal',
        requestBody: { orderId: 'order_xxx', paymentOrderId: 'pay_xxx', gatewayPaymentId: 'gpay_xxx', gatewaySignature: 'sig_xxx' },
        responseExample: { success: true, data: { verified: true, status: 'completed' } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/payment/verify -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"orderId":"order_xxx","paymentOrderId":"pay_xxx"}'`,
      },
      {
        id: 'payment-status',
        method: 'GET',
        path: '/api/v1/points/payment/:orderId/status',
        title: 'Get Payment Status',
        description: 'Get payment status for an order.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        responseExample: { success: true, data: { orderId: 'order_xxx', gateway: 'razorpay', status: 'completed', amount: 250, attempts: 1 } },
        curlExample: `curl http://localhost:3000/api/v1/points/payment/order_xxx/status -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'payment-retry',
        method: 'POST',
        path: '/api/v1/points/payment/:orderId/retry',
        title: 'Retry Payment',
        description: 'Retry a failed payment.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { userId: 'user_123' },
        responseExample: { success: true, data: { paymentOrderId: 'pay_yyy', gateway: 'razorpay', gatewayOrderId: 'order_zzz' } },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/payment/order_xxx/retry -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"userId":"user_123"}'`,
      },
      {
        id: 'payment-webhook',
        method: 'POST',
        path: '/api/v1/points/payment/webhook',
        title: 'Payment Webhook',
        description: 'Handle gateway webhooks (Razorpay/PayU).',
        auth: 'none (signature verified)',
        requestBody: { event: 'payment.captured', payload: { payment: { entity: { order_id: 'order_xxx' } } } },
        responseExample: { success: true, message: 'Webhook processed' },
        curlExample: `curl -X POST http://localhost:3000/api/v1/points/payment/webhook -H "Content-Type: application/json" -H "x-razorpay-signature: sig_xxx" -d '{"event":"payment.captured"}'`,
      },
    ],
  },
  {
    id: 'notifications',
    name: 'Notification APIs',
    icon: '🔔',
    endpoints: [
      {
        id: 'notif-preferences',
        method: 'GET',
        path: '/api/v1/points/notifications/preferences/:userId',
        title: 'Get Preferences',
        description: 'Get user notification preferences.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        responseExample: { success: true, data: { enabled: true, channels: { whatsapp: true, sms: false }, events: { points_earned: true } } },
        curlExample: `curl http://localhost:3000/api/v1/points/notifications/preferences/user_123 -H "x-api-key: YOUR_API_KEY"`,
      },
      {
        id: 'notif-update-preferences',
        method: 'PUT',
        path: '/api/v1/points/notifications/preferences/:userId',
        title: 'Update Preferences',
        description: 'Update user notification preferences.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        requestBody: { enabled: true, channels: { whatsapp: true }, events: { points_earned: true } },
        responseExample: { success: true, data: { enabled: true, channels: { whatsapp: true } } },
        curlExample: `curl -X PUT http://localhost:3000/api/v1/points/notifications/preferences/user_123 -H "Content-Type: application/json" -H "x-api-key: YOUR_API_KEY" -d '{"enabled":true}'`,
      },
      {
        id: 'notif-history',
        method: 'GET',
        path: '/api/v1/points/notifications/history/:userId',
        title: 'Get Notification History',
        description: 'Get notification history for a user.',
        auth: 'mobile | internal',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page', default: '1' },
          { name: 'limit', type: 'number', required: false, description: 'Limit', default: '20' },
          { name: 'eventId', type: 'string', required: false, description: 'Filter by event' },
        ],
        responseExample: { success: true, data: { notifications: [{ id: 'n_xxx', eventId: 'points_earned', channel: 'push', status: 'sent' }], hasMore: false } },
        curlExample: `curl "http://localhost:3000/api/v1/points/notifications/history/user_123?limit=20" -H "x-api-key: YOUR_API_KEY"`,
      },
    ],
  },
  {
    id: 'admin-catalog',
    name: 'Admin: Catalog',
    icon: '📦',
    endpoints: [
      { id: 'admin-cat-list', method: 'GET', path: '/api/admin/points/catalog/categories', title: 'List Categories', description: 'Get all categories.', auth: 'admin', queryParams: [{ name: 'includeInactive', type: 'boolean', required: false, description: 'Include inactive', default: 'false' }], responseExample: { success: true, data: { categories: [] } }, curlExample: `curl http://localhost:3000/api/admin/points/catalog/categories -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-cat-get', method: 'GET', path: '/api/admin/points/catalog/categories/:id', title: 'Get Category', description: 'Get category by ID.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Category ID' }], responseExample: { success: true, data: { id: 'cat_xxx' } }, curlExample: `curl http://localhost:3000/api/admin/points/catalog/categories/cat_xxx -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-cat-create', method: 'POST', path: '/api/admin/points/catalog/categories', title: 'Create Category', description: 'Create new category.', auth: 'admin', requestBody: { name: 'electricity', displayName: 'Electricity' }, responseExample: { success: true, data: { id: 'cat_xxx' } }, curlExample: `curl -X POST http://localhost:3000/api/admin/points/catalog/categories -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"name":"electricity","displayName":"Electricity"}'` },
      { id: 'admin-cat-update', method: 'PUT', path: '/api/admin/points/catalog/categories/:id', title: 'Update Category', description: 'Update category.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Category ID' }], requestBody: { displayName: 'Updated', isActive: true }, responseExample: { success: true, data: { id: 'cat_xxx' } }, curlExample: `curl -X PUT http://localhost:3000/api/admin/points/catalog/categories/cat_xxx -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"isActive":true}'` },
      { id: 'admin-cat-delete', method: 'DELETE', path: '/api/admin/points/catalog/categories/:id', title: 'Delete Category', description: 'Soft delete category.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Category ID' }], responseExample: { success: true, message: 'Deleted' }, curlExample: `curl -X DELETE http://localhost:3000/api/admin/points/catalog/categories/cat_xxx -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-op-list', method: 'GET', path: '/api/admin/points/catalog/operators', title: 'List Operators', description: 'Get operators.', auth: 'admin', queryParams: [{ name: 'categoryId', type: 'string', required: false, description: 'Filter by category' }, { name: 'q', type: 'string', required: false, description: 'Search' }], responseExample: { success: true, data: { operators: [] } }, curlExample: `curl "http://localhost:3000/api/admin/points/catalog/operators?categoryId=mobile" -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-op-get', method: 'GET', path: '/api/admin/points/catalog/operators/:id', title: 'Get Operator', description: 'Get operator by ID.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Operator ID' }], responseExample: { success: true, data: { id: 'op_xxx' } }, curlExample: `curl http://localhost:3000/api/admin/points/catalog/operators/op_xxx -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-op-create', method: 'POST', path: '/api/admin/points/catalog/operators', title: 'Create Operator', description: 'Create operator.', auth: 'admin', requestBody: { pgBillerId: 'BILLER_JIO', categoryId: 'mobile', displayName: 'Jio' }, responseExample: { success: true, data: { id: 'op_xxx' } }, curlExample: `curl -X POST http://localhost:3000/api/admin/points/catalog/operators -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"pgBillerId":"BILLER_JIO","categoryId":"mobile","displayName":"Jio"}'` },
      { id: 'admin-op-update', method: 'PUT', path: '/api/admin/points/catalog/operators/:id', title: 'Update Operator', description: 'Update operator.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Operator ID' }], requestBody: { displayName: 'Updated', isActive: true }, responseExample: { success: true, data: { id: 'op_xxx' } }, curlExample: `curl -X PUT http://localhost:3000/api/admin/points/catalog/operators/op_xxx -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"isActive":true}'` },
      { id: 'admin-op-delete', method: 'DELETE', path: '/api/admin/points/catalog/operators/:id', title: 'Delete Operator', description: 'Soft delete operator.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Operator ID' }], responseExample: { success: true, message: 'Deleted' }, curlExample: `curl -X DELETE http://localhost:3000/api/admin/points/catalog/operators/op_xxx -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-plan-list', method: 'GET', path: '/api/admin/points/catalog/plans', title: 'List Plans', description: 'Get plans by operator.', auth: 'admin', queryParams: [{ name: 'operatorId', type: 'string', required: true, description: 'Operator ID' }], responseExample: { success: true, data: { plans: [] } }, curlExample: `curl "http://localhost:3000/api/admin/points/catalog/plans?operatorId=op_jio" -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-plan-get', method: 'GET', path: '/api/admin/points/catalog/plans/:id', title: 'Get Plan', description: 'Get plan by ID.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Plan ID' }], responseExample: { success: true, data: { id: 'plan_xxx' } }, curlExample: `curl http://localhost:3000/api/admin/points/catalog/plans/plan_xxx -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-plan-create', method: 'POST', path: '/api/admin/points/catalog/plans', title: 'Create Plan', description: 'Create plan.', auth: 'admin', requestBody: { operatorId: 'op_jio', categoryId: 'mobile', pgPlanId: 'PLAN_199', amount: 199 }, responseExample: { success: true, data: { id: 'plan_xxx' } }, curlExample: `curl -X POST http://localhost:3000/api/admin/points/catalog/plans -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"operatorId":"op_jio","categoryId":"mobile","pgPlanId":"PLAN_199","amount":199}'` },
      { id: 'admin-plan-update', method: 'PUT', path: '/api/admin/points/catalog/plans/:id', title: 'Update Plan', description: 'Update plan.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Plan ID' }], requestBody: { isActive: true, featured: true }, responseExample: { success: true, data: { id: 'plan_xxx' } }, curlExample: `curl -X PUT http://localhost:3000/api/admin/points/catalog/plans/plan_xxx -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"isActive":true}'` },
      { id: 'admin-plan-delete', method: 'DELETE', path: '/api/admin/points/catalog/plans/:id', title: 'Delete Plan', description: 'Soft delete plan.', auth: 'admin', pathParams: [{ name: 'id', type: 'string', required: true, description: 'Plan ID' }], responseExample: { success: true, message: 'Deleted' }, curlExample: `curl -X DELETE http://localhost:3000/api/admin/points/catalog/plans/plan_xxx -H "x-api-key: YOUR_ADMIN_KEY"` },
      { id: 'admin-sync-plans', method: 'POST', path: '/api/admin/points/catalog/operators/:operatorId/sync-plans', title: 'Sync Plans', description: 'Bulk sync plans.', auth: 'admin', pathParams: [{ name: 'operatorId', type: 'string', required: true, description: 'Operator ID' }], requestBody: { categoryId: 'mobile', plans: [] }, responseExample: { success: true, data: { created: 5, updated: 10 } }, curlExample: `curl -X POST http://localhost:3000/api/admin/points/catalog/operators/op_jio/sync-plans -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"categoryId":"mobile","plans":[]}'` },
    ],
  },
  {
    id: 'admin-notifications',
    name: 'Admin: Notifications',
    icon: '🔔',
    endpoints: [
      {
        id: 'admin-notif-events',
        method: 'GET',
        path: '/api/admin/points/notifications/events',
        title: 'List Events',
        description: 'Get all notification events.',
        auth: 'admin',
        responseExample: { success: true, data: { events: [{ id: 'points_earned', name: 'Points Earned', channels: ['push', 'whatsapp'] }], total: 5 } },
        curlExample: `curl http://localhost:3000/api/admin/points/notifications/events -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-notif-templates',
        method: 'GET',
        path: '/api/admin/points/notifications/templates',
        title: 'List Templates',
        description: 'Get all notification templates.',
        auth: 'admin',
        responseExample: { success: true, data: { templates: [{ id: 'tmpl_xxx', eventId: 'points_earned', channel: 'push', title: 'You earned points!' }], total: 10 } },
        curlExample: `curl http://localhost:3000/api/admin/points/notifications/templates -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-notif-trigger',
        method: 'POST',
        path: '/api/admin/points/notifications/trigger',
        title: 'Trigger Notification',
        description: 'Trigger a notification for a user.',
        auth: 'admin',
        requestBody: { userId: 'user_123', eventId: 'points_earned', data: { points: 5000 }, priority: 'high' },
        responseExample: { success: true, data: { success: true, status: 'sent', channels: ['push'] } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/notifications/trigger -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"userId":"user_123","eventId":"points_earned"}'`,
      },
      {
        id: 'admin-notif-test',
        method: 'POST',
        path: '/api/admin/points/notifications/test',
        title: 'Send Test',
        description: 'Send a test notification to a user.',
        auth: 'admin',
        requestBody: { userId: 'user_123', channel: 'push', message: 'This is a test notification' },
        responseExample: { success: true, data: { success: true, status: 'sent' } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/notifications/test -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"userId":"user_123","channel":"push","message":"Test"}'`,
      },
      {
        id: 'admin-notif-bulk',
        method: 'POST',
        path: '/api/admin/points/notifications/bulk',
        title: 'Bulk Send',
        description: 'Send notification to multiple users.',
        auth: 'admin',
        requestBody: { userIds: ['user_1', 'user_2'], eventId: 'points_expiring', data: { expiringPoints: 1000 } },
        responseExample: { success: true, data: { totalSent: 2, successCount: 2, failureCount: 0 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/notifications/bulk -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"userIds":["user_1"],"eventId":"points_expiring"}'`,
      },
    ],
  },
  {
    id: 'admin-config',
    name: 'Admin: Configuration',
    icon: '⚙️',
    endpoints: [
      {
        id: 'config-get-all',
        method: 'GET',
        path: '/api/admin/points/config',
        title: 'Get All Configurations',
        description: 'Get all system configurations.',
        auth: 'admin',
        responseExample: { success: true, data: { earning: {}, expiry: {}, redemption: {}, payment_gateway: {}, migration: {}, notifications: {} } },
        curlExample: `curl http://localhost:3000/api/admin/points/config -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'config-initialize',
        method: 'POST',
        path: '/api/admin/points/config/initialize',
        title: 'Initialize Configurations',
        description: 'Initialize all configs with defaults.',
        auth: 'admin',
        responseExample: { success: true, message: 'All configurations initialized', data: {} },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/config/initialize -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'config-get',
        method: 'GET',
        path: '/api/admin/points/config/:type',
        title: 'Get Configuration',
        description: 'Get config by type: earning, expiry, redemption, payment_gateway, migration, notifications.',
        auth: 'admin',
        pathParams: [{ name: 'type', type: 'string', required: true, description: 'Config type' }],
        responseExample: { success: true, data: { configType: 'earning', tiers: [], welcomeBonus: { enabled: true } } },
        curlExample: `curl http://localhost:3000/api/admin/points/config/earning -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'config-update',
        method: 'PUT',
        path: '/api/admin/points/config/:type',
        title: 'Update Configuration',
        description: 'Update config with validation and audit.',
        auth: 'admin',
        pathParams: [{ name: 'type', type: 'string', required: true, description: 'Config type' }],
        requestBody: { welcomeBonus: { enabled: true, points: 15000 } },
        responseExample: { success: true, message: 'Configuration updated', data: { version: 3 } },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/config/earning -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"welcomeBonus":{"points":15000}}'`,
      },
      {
        id: 'config-clear-cache',
        method: 'PUT',
        path: '/api/admin/points/config/:type/clear-cache',
        title: 'Clear Config Cache',
        description: 'Clear configuration cache (for debugging).',
        auth: 'admin',
        pathParams: [{ name: 'type', type: 'string', required: true, description: 'Config type or "all"' }],
        responseExample: { success: true, message: 'Configuration cache cleared' },
        curlExample: `curl -X PATCH http://localhost:3000/api/admin/points/config/earning/clear-cache -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
    ],
  },
  {
    id: 'admin-users',
    name: 'Admin: Users',
    icon: '👥',
    endpoints: [
      {
        id: 'admin-search-users',
        method: 'GET',
        path: '/api/admin/points/users',
        title: 'Search Users',
        description: 'Search users by user ID prefix.',
        auth: 'admin',
        queryParams: [
          { name: 'q', type: 'string', required: true, description: 'Search term (userId prefix)' },
          { name: 'limit', type: 'number', required: false, description: 'Max results', default: '20' },
        ],
        responseExample: { success: true, data: { users: [{ userId: 'user_123', totalPoints: 25000, availablePoints: 20000, lastActivityAt: '2026-01-17T10:00:00Z' }], count: 1 } },
        curlExample: `curl "http://localhost:3000/api/admin/points/users?q=user_&limit=20" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-user-details',
        method: 'GET',
        path: '/api/admin/points/users/:userId',
        title: 'Get User Details',
        description: 'Get detailed user info including balance, buckets, and recent transactions.',
        auth: 'admin',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        responseExample: { success: true, data: { balance: { totalPoints: 25000 }, buckets: [{ type: 'earned', points: 20000, expiresAt: '2027-01-17' }], recentTransactions: [] } },
        curlExample: `curl http://localhost:3000/api/admin/points/users/user_123 -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-adjust',
        method: 'POST',
        path: '/api/admin/points/users/:userId/adjust',
        title: 'Adjust Points',
        description: 'Manually credit or debit points for a user.',
        auth: 'admin',
        pathParams: [{ name: 'userId', type: 'string', required: true, description: 'User ID' }],
        requestBody: { points: 5000, reason: 'Customer goodwill credit', approvalReference: 'TICKET-123' },
        responseExample: { success: true, message: 'Successfully credited 5000 points', data: { transactionId: 'txn_xxx', pointsAdjusted: 5000, newBalance: 30000 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/users/user_123/adjust \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"points": 5000, "reason": "Customer goodwill credit"}'`,
      },
    ],
  },
  {
    id: 'admin-expiry',
    name: 'Admin: Expiry',
    icon: '⏰',
    endpoints: [
      {
        id: 'expiry-stats',
        method: 'GET',
        path: '/api/admin/points/expiry/stats',
        title: 'Get Expiry Stats',
        description: 'Get statistics about expiring points.',
        auth: 'admin',
        responseExample: { success: true, data: { totalUsersWithExpiringPoints: 150, pointsExpiringNext7Days: 50000, pointsExpiringNext30Days: 200000 } },
        curlExample: `curl http://localhost:3000/api/admin/points/expiry/stats -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'expiry-run',
        method: 'POST',
        path: '/api/admin/points/expiry/run',
        title: 'Run Expiry Job',
        description: 'Manually trigger expiry processing.',
        auth: 'admin',
        responseExample: { success: true, data: { usersProcessed: 50, pointsExpired: 25000, notificationsSent: 45 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/expiry/run -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'expiry-preview',
        method: 'GET',
        path: '/api/admin/points/expiry/preview',
        title: 'Preview Expiry',
        description: 'Preview what would expire if job runs now.',
        auth: 'admin',
        responseExample: { success: true, data: { usersAffected: 50, totalPointsToExpire: 25000, buckets: [] } },
        curlExample: `curl http://localhost:3000/api/admin/points/expiry/preview -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'expiry-users',
        method: 'GET',
        path: '/api/admin/points/expiry/users',
        title: 'Users with Expiring Points',
        description: 'Get users with points expiring soon.',
        auth: 'admin',
        queryParams: [
          { name: 'days', type: 'number', required: false, description: 'Days threshold', default: '30' },
          { name: 'limit', type: 'number', required: false, description: 'Max results', default: '50' },
        ],
        responseExample: { success: true, data: { daysThreshold: 30, usersCount: 25, users: [{ userId: 'user_123', expiringPoints: 5000 }] } },
        curlExample: `curl "http://localhost:3000/api/admin/points/expiry/users?days=30&limit=50" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'expiry-last-job',
        method: 'GET',
        path: '/api/admin/points/expiry/last-job',
        title: 'Get Last Job Result',
        description: 'Get last expiry job result.',
        auth: 'admin',
        responseExample: { success: true, data: { runAt: '2026-01-17T00:00:00Z', usersProcessed: 50, pointsExpired: 25000 } },
        curlExample: `curl http://localhost:3000/api/admin/points/expiry/last-job -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
    ],
  },
  {
    id: 'admin-migration',
    name: 'Admin: Migration',
    icon: '🔄',
    endpoints: [
      {
        id: 'migration-stats',
        method: 'GET',
        path: '/api/admin/points/migration/stats',
        title: 'Get Migration Stats',
        description: 'Get overall migration statistics.',
        auth: 'admin',
        responseExample: { success: true, data: { totalBatches: 3, completedBatches: 2, totalUsersMigrated: 500, totalPointsMigrated: 5000000 } },
        curlExample: `curl http://localhost:3000/api/admin/points/migration/stats -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'migration-batches',
        method: 'GET',
        path: '/api/admin/points/migration/batches',
        title: 'List Batches',
        description: 'Get all migration batches.',
        auth: 'admin',
        responseExample: { success: true, data: { batches: [{ id: 'batch_xxx', name: 'Jan 2026', status: 'completed' }], total: 1 } },
        curlExample: `curl http://localhost:3000/api/admin/points/migration/batches -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'migration-batch-details',
        method: 'GET',
        path: '/api/admin/points/migration/batches/:id',
        title: 'Get Batch Details',
        description: 'Get batch with migrated users (use ?includeTransactions=true).',
        auth: 'admin',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Batch ID' }],
        queryParams: [{ name: 'includeTransactions', type: 'boolean', required: false, description: 'Include user details', default: 'false' }],
        responseExample: { success: true, data: { id: 'batch_xxx', status: 'completed', migratedUsers: [{ userId: 'u1', pointsMigrated: 5000 }] } },
        curlExample: `curl "http://localhost:3000/api/admin/points/migration/batches/batch_xxx?includeTransactions=true" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'migration-create',
        method: 'POST',
        path: '/api/admin/points/migration/batches',
        title: 'Create Batch',
        description: 'Create a new migration batch.',
        auth: 'admin',
        requestBody: { name: 'Feb 2026 Migration', userFilter: { minBalance: 100 } },
        responseExample: { success: true, data: { id: 'batch_yyy', name: 'Feb 2026', status: 'pending' } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/migration/batches -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"name":"Feb 2026"}'`,
      },
      {
        id: 'migration-execute',
        method: 'POST',
        path: '/api/admin/points/migration/batches/:id/execute',
        title: 'Execute Batch',
        description: 'Execute with user data.',
        auth: 'admin',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Batch ID' }],
        requestBody: { users: [{ userId: 'user_123', legacyPoints: 100 }] },
        responseExample: { success: true, data: { batchId: 'batch_xxx', usersProcessed: 2, totalPointsMigrated: 30000 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/migration/batches/batch_xxx/execute -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"users":[{"userId":"u1","legacyPoints":100}]}'`,
      },
      {
        id: 'migration-rollback',
        method: 'POST',
        path: '/api/admin/points/migration/batches/:id/rollback',
        title: 'Rollback Batch',
        description: 'Rollback completed batch.',
        auth: 'admin',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Batch ID' }],
        responseExample: { success: true, data: { batchId: 'batch_xxx', usersRolledBack: 100 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/migration/batches/batch_xxx/rollback -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'migration-delete',
        method: 'DELETE',
        path: '/api/admin/points/migration/batches/:id',
        title: 'Delete Batch',
        description: 'Delete a pending batch.',
        auth: 'admin',
        pathParams: [{ name: 'id', type: 'string', required: true, description: 'Batch ID' }],
        responseExample: { success: true, message: 'Batch deleted' },
        curlExample: `curl -X DELETE http://localhost:3000/api/admin/points/migration/batches/batch_xxx -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'migration-preview',
        method: 'POST',
        path: '/api/admin/points/migration/preview',
        title: 'Preview Migration',
        description: 'Preview migration without executing.',
        auth: 'admin',
        requestBody: { users: [{ userId: 'user_123', legacyPoints: 100 }] },
        responseExample: { success: true, data: { totalUsers: 1, totalLegacyPoints: 100, totalNewPoints: 10000, conversionRate: 100 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/migration/preview -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"users":[{"userId":"u1","legacyPoints":100}]}'`,
      },
    ],
  },
  {
    id: 'admin-orders',
    name: 'Admin: Orders',
    icon: '🛒',
    endpoints: [
      {
        id: 'admin-orders-list',
        method: 'GET',
        path: '/api/admin/points/redemption/orders',
        title: 'List All Orders',
        description: 'Get all redemption orders with filters.',
        auth: 'admin',
        queryParams: [
          { name: 'page', type: 'number', required: false, description: 'Page', default: '1' },
          { name: 'limit', type: 'number', required: false, description: 'Limit', default: '20' },
          { name: 'status', type: 'string', required: false, description: 'Filter status' },
          { name: 'fromDate', type: 'string', required: false, description: 'From date (ISO)' },
          { name: 'toDate', type: 'string', required: false, description: 'To date (ISO)' },
        ],
        responseExample: { success: true, data: { orders: [{ id: 'order_xxx', userId: 'user_123', amount: 500, status: 'processing' }], hasMore: true } },
        curlExample: `curl "http://localhost:3000/api/admin/points/redemption/orders?status=processing" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-order-details',
        method: 'GET',
        path: '/api/admin/points/redemption/orders/:orderId',
        title: 'Get Order Details',
        description: 'Get full order details.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        responseExample: { success: true, data: { id: 'order_xxx', userId: 'user_123', amount: 500, pointsUsed: 25000, payment: {}, fulfillment: {} } },
        curlExample: `curl http://localhost:3000/api/admin/points/redemption/orders/order_xxx -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-order-complete',
        method: 'POST',
        path: '/api/admin/points/redemption/orders/:orderId/complete',
        title: 'Complete Order',
        description: 'Mark order as completed.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { providerTransactionId: 'recharge_xxx' },
        responseExample: { success: true, message: 'Order completed', data: { orderId: 'order_xxx', status: 'completed' } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/redemption/orders/order_xxx/complete -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"providerTransactionId":"recharge_xxx"}'`,
      },
      {
        id: 'admin-order-refund',
        method: 'POST',
        path: '/api/admin/points/redemption/orders/:orderId/refund',
        title: 'Refund Order',
        description: 'Refund order and restore points.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { reason: 'Recharge failed' },
        responseExample: { success: true, message: 'Order refunded', data: { orderId: 'order_xxx', pointsRefunded: 25000 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/redemption/orders/order_xxx/refund -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"reason":"Failed"}'`,
      },
      {
        id: 'admin-order-fulfillment',
        method: 'PUT',
        path: '/api/admin/points/redemption/orders/:orderId/fulfillment',
        title: 'Update Fulfillment',
        description: 'Update fulfillment status.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { status: 'completed', providerTransactionId: 'recharge_xxx' },
        responseExample: { success: true, data: { orderId: 'order_xxx', fulfillment: { status: 'completed' } } },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/redemption/orders/order_xxx/fulfillment -H "Content-Type: application/json" -H "x-api-key: YOUR_ADMIN_KEY" -d '{"status":"completed"}'`,
      },
    ],
  },
  {
    id: 'admin-dashboard',
    name: 'Admin: Dashboard',
    icon: '📊',
    endpoints: [
      {
        id: 'dashboard-stats',
        method: 'GET',
        path: '/api/admin/points/dashboard',
        title: 'Get Dashboard Stats',
        description: 'Get main dashboard statistics including points in circulation, active users, and daily metrics.',
        auth: 'admin',
        responseExample: { success: true, data: { totalPointsInCirculation: 50000000, activeUsers30Days: 5000, pointsEarnedToday: 250000, pointsRedeemedToday: 100000, generatedAt: '2026-01-17T10:00:00Z' } },
        curlExample: `curl http://localhost:3000/api/admin/points/dashboard -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'dashboard-health',
        method: 'GET',
        path: '/api/admin/points/dashboard/health',
        title: 'Health Check',
        description: 'Check system health and service connectivity.',
        auth: 'admin',
        responseExample: { success: true, data: { status: 'healthy', services: { cosmosDb: { enabled: true, connected: true } }, timestamp: '2026-01-17T10:00:00Z' } },
        curlExample: `curl http://localhost:3000/api/admin/points/dashboard/health -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
    ],
  },
  {
    id: 'admin-audit',
    name: 'Admin: Audit',
    icon: '📋',
    endpoints: [
      {
        id: 'audit-logs',
        method: 'GET',
        path: '/api/admin/points/audit/logs',
        title: 'Query Audit Logs',
        description: 'Query audit logs with filters.',
        auth: 'admin',
        queryParams: [
          { name: 'action', type: 'string', required: false, description: 'Filter by action' },
          { name: 'actor', type: 'string', required: false, description: 'Filter by actor' },
          { name: 'actorType', type: 'string', required: false, description: 'admin | system | api' },
          { name: 'resourceType', type: 'string', required: false, description: 'Resource type' },
          { name: 'startDate', type: 'string', required: false, description: 'From date (ISO)' },
          { name: 'endDate', type: 'string', required: false, description: 'To date (ISO)' },
          { name: 'limit', type: 'number', required: false, description: 'Max results', default: '50' },
        ],
        responseExample: { success: true, data: { logs: [{ id: 'log_xxx', action: 'CONFIG_UPDATE', actor: 'admin' }], total: 100 } },
        curlExample: `curl "http://localhost:3000/api/admin/points/audit/logs?action=CONFIG_UPDATE&limit=50" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'audit-logs-export',
        method: 'GET',
        path: '/api/admin/points/audit/logs/export',
        title: 'Export Audit Logs',
        description: 'Export logs as JSON or CSV.',
        auth: 'admin',
        queryParams: [
          { name: 'format', type: 'string', required: false, description: 'json | csv', default: 'json' },
          { name: 'limit', type: 'number', required: false, description: 'Max records', default: '1000' },
        ],
        responseExample: { contentType: 'application/json', filename: 'audit-logs.json' },
        curlExample: `curl "http://localhost:3000/api/admin/points/audit/logs/export?format=csv" -H "x-api-key: YOUR_ADMIN_KEY" -o audit.csv`,
      },
      {
        id: 'audit-dashboard',
        method: 'GET',
        path: '/api/admin/points/audit/dashboard',
        title: 'Dashboard Stats',
        description: 'Get comprehensive dashboard statistics.',
        auth: 'admin',
        responseExample: { success: true, data: { points: { totalEarned: 50000000, totalRedeemed: 20000000 }, users: { totalUsers: 10000 } } },
        curlExample: `curl http://localhost:3000/api/admin/points/audit/dashboard -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'audit-activity',
        method: 'GET',
        path: '/api/admin/points/audit/activity/:period',
        title: 'Activity Summary',
        description: 'Get activity for today, week, or month.',
        auth: 'admin',
        pathParams: [{ name: 'period', type: 'string', required: true, description: 'today | week | month' }],
        responseExample: { success: true, data: { earnTransactions: 500, earnPoints: 250000, redeemTransactions: 100 } },
        curlExample: `curl http://localhost:3000/api/admin/points/audit/activity/today -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'audit-actions',
        method: 'GET',
        path: '/api/admin/points/audit/actions',
        title: 'Action Summary',
        description: 'Get action summary for last N days.',
        auth: 'admin',
        queryParams: [{ name: 'days', type: 'number', required: false, description: 'Days', default: '7' }],
        responseExample: { success: true, data: { period: 'Last 7 days', actions: { CONFIG_UPDATE: 5, POINTS_ADJUST: 10 } } },
        curlExample: `curl "http://localhost:3000/api/admin/points/audit/actions?days=7" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'audit-recent',
        method: 'GET',
        path: '/api/admin/points/audit/recent',
        title: 'Recent Logs',
        description: 'Get recent audit logs.',
        auth: 'admin',
        queryParams: [{ name: 'limit', type: 'number', required: false, description: 'Limit', default: '50' }],
        responseExample: { success: true, data: { logs: [], count: 50 } },
        curlExample: `curl "http://localhost:3000/api/admin/points/audit/recent?limit=50" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
    ],
  },
  {
    id: 'admin-bbps',
    name: 'Admin: BBPS Gateway',
    icon: '🔌',
    endpoints: [
      {
        id: 'bbps-config-get',
        method: 'GET',
        path: '/api/admin/points/bbps/config',
        title: 'Get BBPS Config',
        description: 'Get current BBPS gateway configuration including PayU and Razorpay settings (credentials masked).',
        auth: 'admin',
        responseExample: {
          success: true,
          config: {
            activeProvider: 'payu',
            fallbackProvider: 'razorpay',
            payu: {
              enabled: true,
              environment: 'test',
              credentials: { clientId: 'payu_client_xxx', clientSecretRef: '***configured***', agentId: 'agent_xxx' },
              endpoints: { authHost: 'https://uat-accounts.payu.in', nbcHost: 'https://bbps-sb.payu.in' }
            },
            catalogSync: { enabled: true, intervalHours: 24, lastSyncAt: '2026-01-19T10:00:00Z', lastSyncStatus: 'success' },
            healthCheck: { enabled: true, intervalMinutes: 5 }
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/config -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-config-summary',
        method: 'GET',
        path: '/api/admin/points/bbps/config/summary',
        title: 'Get Config Summary',
        description: 'Get configuration summary without sensitive data, suitable for dashboard display.',
        auth: 'admin',
        responseExample: {
          success: true,
          summary: {
            activeProvider: 'payu',
            fallbackProvider: 'razorpay',
            payuEnabled: true,
            payuEnvironment: 'test',
            razorpayEnabled: false,
            catalogSyncEnabled: true,
            lastSyncAt: '2026-01-19T10:00:00Z',
            lastSyncStatus: 'success',
            healthCheckEnabled: true
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/config/summary -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-set-active-provider',
        method: 'PUT',
        path: '/api/admin/points/bbps/config/active-provider',
        title: 'Set Active Provider',
        description: 'Switch the active BBPS provider. Provider must be enabled and configured. Changes take effect immediately without restart.',
        auth: 'admin',
        requestBody: { provider: 'payu' },
        responseExample: { success: true, message: 'Active provider set to payu', activeProvider: 'payu' },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/bbps/config/active-provider \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider": "payu"}'`,
      },
      {
        id: 'bbps-set-fallback-provider',
        method: 'PUT',
        path: '/api/admin/points/bbps/config/fallback-provider',
        title: 'Set Fallback Provider',
        description: 'Set fallback BBPS provider for automatic failover when primary fails.',
        auth: 'admin',
        requestBody: { provider: 'razorpay' },
        responseExample: { success: true, message: 'Fallback provider set to razorpay', fallbackProvider: 'razorpay' },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/bbps/config/fallback-provider \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider": "razorpay"}'`,
      },
      {
        id: 'bbps-update-payu',
        method: 'PUT',
        path: '/api/admin/points/bbps/config/payu',
        title: 'Update PayU Config',
        description: 'Update PayU BBPS configuration including credentials, environment, and endpoints.',
        auth: 'admin',
        requestBody: {
          enabled: true,
          environment: 'test',
          credentials: { clientId: 'your_client_id', clientSecretRef: 'your_secret_or_keyvault_ref', agentId: 'your_agent_id', grantType: 'client_credentials' },
          endpoints: { authHost: 'https://uat-accounts.payu.in', nbcHost: 'https://bbps-sb.payu.in' },
          tokenCache: { enabled: true, marginSeconds: 300 }
        },
        responseExample: { success: true, message: 'PayU configuration updated', payu: { enabled: true, environment: 'test' } },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/bbps/config/payu \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"enabled": true, "environment": "test"}'`,
      },
      {
        id: 'bbps-update-razorpay',
        method: 'PUT',
        path: '/api/admin/points/bbps/config/razorpay',
        title: 'Update Razorpay Config',
        description: 'Update Razorpay BBPS configuration including credentials, environment, and endpoints.',
        auth: 'admin',
        requestBody: {
          enabled: true,
          environment: 'test',
          credentials: { keyId: 'your_key_id', keySecretRef: 'your_secret_or_keyvault_ref' },
          endpoints: { baseUrl: 'https://api.razorpay.com' }
        },
        responseExample: { success: true, message: 'Razorpay configuration updated', razorpay: { enabled: true, environment: 'test' } },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/bbps/config/razorpay \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"enabled": true, "environment": "test"}'`,
      },
      {
        id: 'bbps-update-catalog-sync',
        method: 'PUT',
        path: '/api/admin/points/bbps/config/catalog-sync',
        title: 'Update Catalog Sync Settings',
        description: 'Configure automatic catalog synchronization from BBPS provider.',
        auth: 'admin',
        requestBody: { enabled: true, intervalHours: 24 },
        responseExample: { success: true, message: 'Catalog sync settings updated', catalogSync: { enabled: true, intervalHours: 24 } },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/bbps/config/catalog-sync \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"enabled": true, "intervalHours": 24}'`,
      },
      {
        id: 'bbps-update-health-check',
        method: 'PUT',
        path: '/api/admin/points/bbps/config/health-check',
        title: 'Update Health Check Settings',
        description: 'Configure automatic health monitoring for BBPS providers.',
        auth: 'admin',
        requestBody: { enabled: true, intervalMinutes: 5 },
        responseExample: { success: true, message: 'Health check settings updated', healthCheck: { enabled: true, intervalMinutes: 5 } },
        curlExample: `curl -X PUT http://localhost:3000/api/admin/points/bbps/config/health-check \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"enabled": true, "intervalMinutes": 5}'`,
      },
      {
        id: 'bbps-health',
        method: 'GET',
        path: '/api/admin/points/bbps/health',
        title: 'Get All Providers Health',
        description: 'Get health status of all configured BBPS providers including response times.',
        auth: 'admin',
        responseExample: {
          success: true,
          activeProvider: 'payu',
          availableProviders: ['payu'],
          health: [
            { provider: 'payu', isHealthy: true, lastCheckedAt: '2026-01-19T10:00:00Z', responseTimeMs: 150 }
          ]
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/health -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-health-check',
        method: 'POST',
        path: '/api/admin/points/bbps/health/check',
        title: 'Check Provider Health',
        description: 'Trigger a health check for a specific BBPS provider.',
        auth: 'admin',
        requestBody: { provider: 'payu' },
        responseExample: {
          success: true,
          health: { provider: 'payu', isHealthy: true, lastCheckedAt: '2026-01-19T10:00:00Z', responseTimeMs: 145 }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/health/check \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider": "payu"}'`,
      },
      {
        id: 'bbps-providers',
        method: 'GET',
        path: '/api/admin/points/bbps/providers',
        title: 'List Providers',
        description: 'Get list of all BBPS providers with their configuration status.',
        auth: 'admin',
        responseExample: {
          success: true,
          activeProvider: 'payu',
          fallbackProvider: 'razorpay',
          providers: [
            { id: 'payu', name: 'PayU BBPS', enabled: true, environment: 'test', isActive: true, isFallback: false, configured: true },
            { id: 'razorpay', name: 'Razorpay BBPS', enabled: false, environment: 'test', isActive: false, isFallback: true, configured: false }
          ]
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/providers -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-factory-initialize',
        method: 'POST',
        path: '/api/admin/points/bbps/factory/initialize',
        title: 'Initialize Factory',
        description: 'Initialize or reinitialize the BBPS gateway factory. Use after configuration changes.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'BBPS gateway factory initialized',
          activeProvider: 'payu',
          availableProviders: ['payu']
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/factory/initialize -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-factory-refresh',
        method: 'POST',
        path: '/api/admin/points/bbps/factory/refresh',
        title: 'Refresh Factory Config',
        description: 'Refresh factory configuration to pick up latest settings without full reinitialization.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'BBPS gateway factory configuration refreshed',
          activeProvider: 'payu',
          availableProviders: ['payu']
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/factory/refresh -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-cache-clear',
        method: 'DELETE',
        path: '/api/admin/points/bbps/cache',
        title: 'Clear Config Cache',
        description: 'Clear BBPS configuration cache to force reload from database.',
        auth: 'admin',
        responseExample: { success: true, message: 'BBPS configuration cache cleared' },
        curlExample: `curl -X DELETE http://localhost:3000/api/admin/points/bbps/cache -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      // Catalog Sync APIs (Phase 12)
      {
        id: 'bbps-catalog-sync',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync',
        title: 'Sync Catalog',
        description: 'Trigger a full catalog sync from the BBPS provider. Syncs categories, operators, and plans. Preserves admin customizations.',
        auth: 'admin',
        requestBody: { provider: 'payu', dryRun: false, syncCategories: true, syncOperators: true, syncPlans: true },
        responseExample: {
          success: true,
          result: {
            success: true,
            provider: 'payu',
            startedAt: '2026-01-19T10:00:00Z',
            completedAt: '2026-01-19T10:00:05Z',
            durationMs: 5000,
            dryRun: false,
            categories: { fetched: 10, created: 2, updated: 8, skipped: 0, errors: 0 },
            operators: { fetched: 50, created: 5, updated: 45, skipped: 0, errors: 0 },
            plans: { fetched: 200, created: 20, updated: 180, skipped: 0, errors: 0 },
            errors: []
          }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider":"payu","dryRun":false}'`,
      },
      {
        id: 'bbps-catalog-sync-status',
        method: 'GET',
        path: '/api/admin/points/bbps/catalog/sync/status',
        title: 'Get Sync Status',
        description: 'Get catalog sync status including last sync result and scheduled job state.',
        auth: 'admin',
        responseExample: {
          success: true,
          status: {
            isRunning: false,
            isScheduledActive: true,
            lastRunAt: '2026-01-19T10:00:00Z',
            lastSyncStatus: { lastSyncAt: '2026-01-19T10:00:00Z', lastSyncSuccess: true, lastSyncProvider: 'payu' }
          },
          lastResult: { success: true, durationMs: 5000 }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/catalog/sync/status -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-catalog-sync-preview',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync/preview',
        title: 'Preview Sync',
        description: 'Preview what would be synced without making any changes. Useful to verify before actual sync.',
        auth: 'admin',
        requestBody: { provider: 'payu' },
        responseExample: {
          success: true,
          preview: {
            success: true,
            dryRun: true,
            categories: { fetched: 10, created: 2, updated: 8, skipped: 0, errors: 0 },
            operators: { fetched: 50, created: 5, updated: 45, skipped: 0, errors: 0 },
            plans: { fetched: 200, created: 20, updated: 180, skipped: 0, errors: 0 }
          }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync/preview \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider":"payu"}'`,
      },
      {
        id: 'bbps-catalog-sync-schedule-start',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync/schedule/start',
        title: 'Start Scheduled Sync',
        description: 'Start automatic scheduled catalog sync. Runs immediately then at specified interval.',
        auth: 'admin',
        requestBody: { intervalHours: 24 },
        responseExample: {
          success: true,
          message: 'Scheduled sync started (every 24 hours)',
          status: { isRunning: false, isScheduledActive: true }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync/schedule/start \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"intervalHours":24}'`,
      },
      {
        id: 'bbps-catalog-sync-schedule-stop',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync/schedule/stop',
        title: 'Stop Scheduled Sync',
        description: 'Stop the scheduled automatic catalog sync job.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'Scheduled sync stopped',
          status: { isRunning: false, isScheduledActive: false }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync/schedule/stop -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-catalog-sync-categories',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync/categories',
        title: 'Sync Categories Only',
        description: 'Sync only categories from the BBPS provider, skipping operators and plans.',
        auth: 'admin',
        requestBody: { provider: 'payu', dryRun: false },
        responseExample: {
          success: true,
          result: { categories: { fetched: 10, created: 2, updated: 8, skipped: 0, errors: 0 }, durationMs: 1500, errors: [] }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync/categories \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider":"payu"}'`,
      },
      {
        id: 'bbps-catalog-sync-operators',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync/operators',
        title: 'Sync Operators Only',
        description: 'Sync only operators from the BBPS provider. Can filter by categoryIds.',
        auth: 'admin',
        requestBody: { provider: 'payu', categoryIds: ['mobile', 'dth'], dryRun: false },
        responseExample: {
          success: true,
          result: { operators: { fetched: 30, created: 3, updated: 27, skipped: 0, errors: 0 }, durationMs: 2500, errors: [] }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync/operators \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider":"payu","categoryIds":["mobile"]}'`,
      },
      {
        id: 'bbps-catalog-sync-plans',
        method: 'POST',
        path: '/api/admin/points/bbps/catalog/sync/plans',
        title: 'Sync Plans Only',
        description: 'Sync only plans from the BBPS provider. Can filter by operatorIds.',
        auth: 'admin',
        requestBody: { provider: 'payu', operatorIds: ['op_jio', 'op_airtel'], dryRun: false },
        responseExample: {
          success: true,
          result: { plans: { fetched: 100, created: 10, updated: 90, skipped: 0, errors: 0 }, durationMs: 3000, errors: [] }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/catalog/sync/plans \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"provider":"payu","operatorIds":["op_jio"]}'`,
      },
      // Bill Operations (Phase 13)
      {
        id: 'bbps-bill-fetch',
        method: 'POST',
        path: '/api/admin/points/bbps/bill/fetch',
        title: 'Fetch Bill',
        description: 'Fetch bill details for a customer. Returns bill amount, due date, and suggested payment split between points and cash.',
        auth: 'admin',
        requestBody: { userId: 'user123', billerId: 'biller_bses_delhi', customerParams: { consumerId: '1234567890' } },
        responseExample: {
          success: true,
          provider: 'payu',
          bill: { success: true, billAmount: 2500, billDate: '2026-01-15', dueDate: '2026-01-31', customerName: 'John Doe', billNumber: 'BILL123456' },
          userPointsBalance: 5000,
          maxPointsAllowed: 2500,
          suggestedSplit: { pointsAmount: 2500, cashAmount: 0, pointsToDeduct: 2500, conversionRate: 1 }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/bill/fetch \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"userId":"user123","billerId":"biller_bses_delhi","customerParams":{"consumerId":"1234567890"}}'`,
      },
      {
        id: 'bbps-bill-pay',
        method: 'POST',
        path: '/api/admin/points/bbps/bill/pay',
        title: 'Pay Bill',
        description: 'Pay a bill using points and/or cash. Points are held during processing and finalized on completion.',
        auth: 'admin',
        requestBody: { userId: 'user123', billerId: 'biller_bses_delhi', amount: 2500, pointsToUse: 1000, customerParams: { consumerId: '1234567890' } },
        responseExample: {
          success: true,
          provider: 'payu',
          orderId: 'order_1705678901234_abc123',
          transactionId: 'order_1705678901234_abc123',
          providerRefId: 'PAYU_REF_123456789',
          status: 'processing',
          pointsUsed: 1000,
          cashAmount: 1500,
          cashPaymentOrderId: 'cash_order_1705678901234_abc123'
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/bill/pay \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"userId":"user123","billerId":"biller_bses_delhi","amount":2500,"pointsToUse":1000,"customerParams":{"consumerId":"1234567890"}}'`,
      },
      {
        id: 'bbps-bill-status-get',
        method: 'GET',
        path: '/api/admin/points/bbps/bill/status/:orderId',
        title: 'Get Bill Status',
        description: 'Check the status of a bill payment by order ID.',
        auth: 'admin',
        responseExample: {
          success: true,
          status: { transactionId: 'order_123', providerRefId: 'PAYU_REF_123', status: 'completed', completedAt: '2026-01-19T12:00:00Z', bbpsRefId: 'BBPS123456' },
          order: { id: 'order_123', userId: 'user123', billerId: 'biller_bses_delhi', amount: 2500, status: 'completed' }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/bill/status/order_123 -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-bill-status-check',
        method: 'POST',
        path: '/api/admin/points/bbps/bill/status/check',
        title: 'Check Bill Status',
        description: 'Check bill payment status by order ID or provider reference ID.',
        auth: 'admin',
        requestBody: { orderId: 'order_123', providerRefId: 'PAYU_REF_123' },
        responseExample: {
          success: true,
          status: { transactionId: 'order_123', providerRefId: 'PAYU_REF_123', status: 'completed', completedAt: '2026-01-19T12:00:00Z' }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/bill/status/check \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"orderId":"order_123"}'`,
      },
      // Status Poller (Phase 13)
      {
        id: 'bbps-poller-status',
        method: 'GET',
        path: '/api/admin/points/bbps/poller/status',
        title: 'Get Poller Status',
        description: 'Get the current status of the transaction status poller including configuration and last poll result.',
        auth: 'admin',
        responseExample: {
          success: true,
          isActive: true,
          isPolling: false,
          lastPollAt: '2026-01-19T12:00:00Z',
          config: { intervalMs: 60000, maxAgeHours: 24, maxRetries: 10, batchSize: 50 },
          lastPollResult: { totalPolled: 5, statusChanges: 2, completions: 1, failures: 1 }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/poller/status -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-poller-start',
        method: 'POST',
        path: '/api/admin/points/bbps/poller/start',
        title: 'Start Poller',
        description: 'Start the status poller with optional configuration. Polls pending transactions at the specified interval.',
        auth: 'admin',
        requestBody: { intervalMs: 60000, maxAgeHours: 24, maxRetries: 10, batchSize: 50 },
        responseExample: {
          success: true,
          message: 'Status poller started (interval: 60000ms)',
          status: { isActive: true, isPolling: false }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/poller/start \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"intervalMs":60000}'`,
      },
      {
        id: 'bbps-poller-stop',
        method: 'POST',
        path: '/api/admin/points/bbps/poller/stop',
        title: 'Stop Poller',
        description: 'Stop the status poller. Pending transactions will not be polled until restarted.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'Status poller stopped',
          status: { isActive: false, isPolling: false }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/poller/stop -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-poller-poll',
        method: 'POST',
        path: '/api/admin/points/bbps/poller/poll',
        title: 'Manual Poll',
        description: 'Trigger a manual poll of all pending transactions. Use for debugging or immediate status updates.',
        auth: 'admin',
        responseExample: {
          success: true,
          result: {
            startedAt: '2026-01-19T12:00:00Z',
            completedAt: '2026-01-19T12:00:05Z',
            durationMs: 5000,
            totalPolled: 10,
            statusChanges: 3,
            completions: 2,
            failures: 1,
            results: [{ orderId: 'order_123', previousStatus: 'processing', newStatus: 'completed', changed: true }]
          }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/poller/poll -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-poller-poll-order',
        method: 'POST',
        path: '/api/admin/points/bbps/poller/poll/:orderId',
        title: 'Poll Single Order',
        description: 'Poll a single order for status update. Useful for debugging specific transactions.',
        auth: 'admin',
        responseExample: {
          success: true,
          result: { orderId: 'order_123', previousStatus: 'processing', newStatus: 'completed', changed: true }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/poller/poll/order_123 -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      // Prepaid Recharge (Phase 14)
      {
        id: 'bbps-prepaid-detect',
        method: 'POST',
        path: '/api/admin/points/bbps/prepaid/detect',
        title: 'Detect Operator',
        description: 'Auto-detect telecom operator and circle from a mobile number. Returns operator name, circle, and IDs for subsequent API calls.',
        auth: 'admin',
        requestBody: { mobileNumber: '9876543210' },
        responseExample: {
          success: true,
          provider: 'payu',
          detection: {
            detected: true,
            operator: { id: 'payu_op_jio', externalId: 'JIO', name: 'Jio', displayName: 'Reliance Jio' },
            circle: { id: 'payu_circle_dl', externalId: 'DL', name: 'Delhi', displayName: 'Delhi NCR' },
            mobileNumber: '9876543210'
          }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/prepaid/detect \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"mobileNumber":"9876543210"}'`,
      },
      {
        id: 'bbps-prepaid-circles',
        method: 'GET',
        path: '/api/admin/points/bbps/prepaid/circles',
        title: 'Get Circles',
        description: 'Get all available telecom circles (regions) for prepaid recharge.',
        auth: 'admin',
        responseExample: {
          success: true,
          provider: 'payu',
          circles: [
            { id: 'payu_circle_dl', externalId: 'DL', name: 'Delhi', displayName: 'Delhi NCR', code: 'DL' },
            { id: 'payu_circle_mh', externalId: 'MH', name: 'Maharashtra', displayName: 'Maharashtra & Goa', code: 'MH' }
          ]
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/prepaid/circles -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-prepaid-operators',
        method: 'GET',
        path: '/api/admin/points/bbps/prepaid/operators',
        title: 'Get Operators',
        description: 'Get all available telecom operators for prepaid recharge.',
        auth: 'admin',
        responseExample: {
          success: true,
          provider: 'payu',
          operators: [
            { id: 'payu_op_jio', externalId: 'JIO', name: 'Jio', displayName: 'Reliance Jio', isActive: true },
            { id: 'payu_op_airtel', externalId: 'AIRTEL', name: 'Airtel', displayName: 'Bharti Airtel', isActive: true },
            { id: 'payu_op_vi', externalId: 'VI', name: 'Vi', displayName: 'Vodafone Idea', isActive: true }
          ]
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/prepaid/operators -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-prepaid-plans',
        method: 'POST',
        path: '/api/admin/points/bbps/prepaid/plans',
        title: 'Get Plans',
        description: 'Get recharge plans for an operator. Can filter by circle and plan type.',
        auth: 'admin',
        requestBody: { operatorId: 'payu_op_jio', circleId: 'payu_circle_dl', planType: 'combo' },
        responseExample: {
          success: true,
          provider: 'payu',
          plans: [
            { id: 'payu_plan_1', name: 'Jio Popular Plan', amount: 299, validity: '28 days', data: '2GB/day', sms: '100/day', planType: 'combo' },
            { id: 'payu_plan_2', name: 'Jio Data Pack', amount: 199, validity: '30 days', data: '25GB', planType: 'data' }
          ],
          totalPlans: 2
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/prepaid/plans \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"operatorId":"payu_op_jio","circleId":"payu_circle_dl"}'`,
      },
      {
        id: 'bbps-prepaid-recharge',
        method: 'POST',
        path: '/api/admin/points/bbps/prepaid/recharge',
        title: 'Execute Recharge',
        description: 'Execute a prepaid mobile recharge using points and/or cash.',
        auth: 'admin',
        requestBody: { userId: 'user123', operatorId: 'payu_op_jio', circleId: 'payu_circle_dl', mobileNumber: '9876543210', amount: 299, planId: 'payu_plan_1', pointsToUse: 100 },
        responseExample: {
          success: true,
          provider: 'payu',
          orderId: 'recharge_1705678901234_abc123',
          transactionId: 'recharge_1705678901234_abc123',
          providerRefId: 'PAYU_REF_987654321',
          status: 'processing',
          pointsUsed: 100,
          cashAmount: 199,
          operatorTxnId: 'JIO_TXN_123456'
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/prepaid/recharge \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -d '{"userId":"user123","operatorId":"payu_op_jio","circleId":"payu_circle_dl","mobileNumber":"9876543210","amount":299,"pointsToUse":100}'`,
      },
      {
        id: 'bbps-prepaid-status',
        method: 'GET',
        path: '/api/admin/points/bbps/prepaid/status/:orderId',
        title: 'Get Recharge Status',
        description: 'Check the status of a prepaid recharge order.',
        auth: 'admin',
        responseExample: {
          success: true,
          order: {
            id: 'recharge_1705678901234_abc123',
            userId: 'user123',
            operatorId: 'payu_op_jio',
            circleId: 'payu_circle_dl',
            mobileNumber: '9876543210',
            amount: 299,
            status: 'completed',
            providerRefId: 'PAYU_REF_987654321',
            operatorTxnId: 'JIO_TXN_123456'
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/prepaid/status/recharge_1705678901234_abc123 -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      // ===== Phase 15: Health Monitoring APIs =====
      {
        id: 'bbps-health',
        method: 'GET',
        path: '/api/admin/points/bbps/health',
        title: 'Get Health Status',
        description: 'Get health status for all BBPS providers including circuit breaker states.',
        auth: 'admin',
        responseExample: {
          success: true,
          health: {
            isActive: true,
            providers: {
              payu: { provider: 'payu', isHealthy: true, lastCheck: '2026-01-19T10:00:00Z', consecutiveSuccesses: 5 },
              razorpay: { provider: 'razorpay', isHealthy: false, lastCheck: '2026-01-19T10:00:00Z', consecutiveFailures: 3, error: 'Connection timeout' }
            },
            config: { intervalMs: 300000, enableAutoFailover: true }
          },
          circuitBreakers: {
            payu: { state: 'CLOSED', failures: 0, successes: 100 },
            razorpay: { state: 'OPEN', failures: 5, consecutiveFailures: 5 }
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/health -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-health-start',
        method: 'POST',
        path: '/api/admin/points/bbps/health/start',
        title: 'Start Health Monitor',
        description: 'Start the automatic health monitoring service with optional configuration.',
        auth: 'admin',
        requestBody: {
          intervalMs: 300000,
          enableAutoFailover: true
        },
        responseExample: {
          success: true,
          message: 'Health monitor started',
          status: { isActive: true, config: { intervalMs: 300000, enableAutoFailover: true } }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/health/start -H "x-api-key: YOUR_ADMIN_KEY" -H "Content-Type: application/json" -d '{"intervalMs":300000,"enableAutoFailover":true}'`,
      },
      {
        id: 'bbps-health-stop',
        method: 'POST',
        path: '/api/admin/points/bbps/health/stop',
        title: 'Stop Health Monitor',
        description: 'Stop the automatic health monitoring service.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'Health monitor stopped',
          status: { isActive: false }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/health/stop -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-health-check',
        method: 'POST',
        path: '/api/admin/points/bbps/health/check',
        title: 'Trigger Health Check',
        description: 'Trigger a manual health check for all BBPS providers.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'Health check completed',
          status: { providers: { payu: { isHealthy: true }, razorpay: { isHealthy: true } } }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/health/check -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-health-check-provider',
        method: 'POST',
        path: '/api/admin/points/bbps/health/check/:provider',
        title: 'Check Provider Health',
        description: 'Trigger a manual health check for a specific provider (payu or razorpay).',
        auth: 'admin',
        pathParams: [{ name: 'provider', type: 'string', required: true, description: 'Provider ID (payu or razorpay)' }],
        responseExample: {
          success: true,
          message: 'Health check completed for payu',
          provider: { provider: 'payu', isHealthy: true, responseTimeMs: 245 }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/health/check/payu -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      // ===== Phase 15: Circuit Breaker APIs =====
      {
        id: 'bbps-circuit-breaker',
        method: 'GET',
        path: '/api/admin/points/bbps/circuit-breaker',
        title: 'Get Circuit Breaker Status',
        description: 'Get circuit breaker status for all BBPS providers.',
        auth: 'admin',
        responseExample: {
          success: true,
          circuitBreakers: {
            payu: { state: 'CLOSED', failures: 0, successes: 100, totalRequests: 100 },
            razorpay: { state: 'HALF_OPEN', failures: 5, successes: 1, consecutiveFailures: 0 }
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/bbps/circuit-breaker -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-circuit-breaker-reset',
        method: 'POST',
        path: '/api/admin/points/bbps/circuit-breaker/reset/:provider',
        title: 'Reset Circuit Breaker',
        description: 'Reset the circuit breaker for a specific provider to CLOSED state.',
        auth: 'admin',
        pathParams: [{ name: 'provider', type: 'string', required: true, description: 'Provider ID to reset (payu or razorpay)' }],
        responseExample: {
          success: true,
          message: 'Circuit breaker reset for payu',
          circuitBreakers: { payu: { state: 'CLOSED', failures: 0, successes: 0 } }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/circuit-breaker/reset/payu -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-circuit-breaker-reset-all',
        method: 'POST',
        path: '/api/admin/points/bbps/circuit-breaker/reset-all',
        title: 'Reset All Circuit Breakers',
        description: 'Reset all circuit breakers to CLOSED state.',
        auth: 'admin',
        responseExample: {
          success: true,
          message: 'All circuit breakers reset',
          circuitBreakers: { payu: { state: 'CLOSED' }, razorpay: { state: 'CLOSED' } }
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/circuit-breaker/reset-all -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      // ===== Phase 15: Audit Log APIs =====
      {
        id: 'bbps-audit',
        method: 'GET',
        path: '/api/admin/points/bbps/audit',
        title: 'Query Audit Logs',
        description: 'Query BBPS audit logs with optional filters for provider, event type, date range, etc.',
        auth: 'admin',
        queryParams: [
          { name: 'provider', type: 'string', description: 'Filter by provider (payu, razorpay)', required: false },
          { name: 'eventType', type: 'string', description: 'Filter by event type (BILL_PAYMENT, RECHARGE, etc.)', required: false },
          { name: 'startDate', type: 'string', description: 'Start date (ISO format)', required: false },
          { name: 'endDate', type: 'string', description: 'End date (ISO format)', required: false },
          { name: 'userId', type: 'string', description: 'Filter by user ID', required: false },
          { name: 'orderId', type: 'string', description: 'Filter by order ID', required: false },
          { name: 'limit', type: 'number', description: 'Max results (default 50)', required: false }
        ],
        responseExample: {
          success: true,
          count: 2,
          logs: [
            { id: 'audit_123', eventType: 'BILL_PAYMENT', provider: 'payu', success: true, durationMs: 1250, timestamp: '2026-01-19T10:00:00Z' },
            { id: 'audit_124', eventType: 'RECHARGE', provider: 'razorpay', success: false, errorMessage: 'Timeout', timestamp: '2026-01-19T09:55:00Z' }
          ]
        },
        curlExample: `curl "http://localhost:3000/api/admin/points/bbps/audit?provider=payu&limit=20" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'bbps-audit-configure',
        method: 'POST',
        path: '/api/admin/points/bbps/audit/configure',
        title: 'Configure Audit Logger',
        description: 'Configure the BBPS audit logger settings.',
        auth: 'admin',
        requestBody: {
          enabled: true,
          logToConsole: true,
          logToCosmos: true,
          retentionDays: 90
        },
        responseExample: {
          success: true,
          message: 'Audit logger configured'
        },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/bbps/audit/configure -H "x-api-key: YOUR_ADMIN_KEY" -H "Content-Type: application/json" -d '{"enabled":true,"retentionDays":90}'`,
      },
    ],
  },
  {
    id: 'admin-mixed-orders',
    name: 'Admin: Mixed Redemption',
    icon: '💳',
    endpoints: [
      {
        id: 'admin-mixed-list',
        method: 'GET',
        path: '/api/admin/points/mixed-orders',
        title: 'List Mixed Orders',
        description: 'Get all mixed redemption orders with filtering and pagination.',
        auth: 'admin',
        queryParams: [
          { name: 'limit', type: 'number', required: false, description: 'Max results', default: '20' },
          { name: 'offset', type: 'number', required: false, description: 'Skip results', default: '0' },
          { name: 'status', type: 'string', required: false, description: 'Filter by status (payment_pending, processing, completed, failed)' },
        ],
        responseExample: {
          success: true,
          data: {
            orders: [
              {
                id: 'MIX-xxx',
                userId: 'user_123',
                status: 'completed',
                amount: 500,
                pointsUsed: 300,
                cashAmount: 200,
                categoryName: 'Electricity',
                paymentStatus: 'payment_captured',
                razorpayOrderId: 'order_abc',
                createdAt: '2026-01-18T12:15:00Z'
              }
            ],
            hasMore: false,
            limit: 20,
            offset: 0
          }
        },
        curlExample: `curl "http://localhost:3000/api/admin/points/mixed-orders?limit=20&status=completed" -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-mixed-stats',
        method: 'GET',
        path: '/api/admin/points/mixed-orders/stats',
        title: 'Mixed Orders Statistics',
        description: 'Get aggregate statistics for mixed redemption orders.',
        auth: 'admin',
        responseExample: {
          success: true,
          data: {
            totalOrders: 150,
            pendingOrders: 5,
            completedOrders: 130,
            failedOrders: 15,
            totalPointsRedeemed: 450000,
            totalCashCollected: 75000
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/mixed-orders/stats -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-mixed-get',
        method: 'GET',
        path: '/api/admin/points/mixed-orders/:orderId',
        title: 'Get Order Details',
        description: 'Get full details of a mixed redemption order including payment and fulfillment info.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        responseExample: {
          success: true,
          data: {
            order: {
              id: 'MIX-xxx',
              userId: 'user_123',
              status: 'completed',
              amount: 500,
              pointsUsed: 300,
              pointsValue: 300,
              cashAmount: 200,
              categoryId: 'electricity',
              categoryName: 'Electricity',
              serviceDetails: { type: 'electricity', beneficiary: '1234567890' },
              payment: { required: true, gateway: 'razorpay', orderId: 'order_abc', paymentId: 'pay_xyz', status: 'completed' },
              mixedPayment: {
                razorpayOrderId: 'order_abc',
                razorpayPaymentId: 'pay_xyz',
                amountInPaise: 20000,
                cashAmount: 200,
                pointsValue: 300,
                pointsUsed: 300,
                paymentStatus: 'payment_captured',
                paymentCapturedAt: '2026-01-18T12:20:00Z'
              },
              fulfillment: { status: 'completed', completedAt: '2026-01-18T12:25:00Z' },
              createdAt: '2026-01-18T12:15:00Z'
            }
          }
        },
        curlExample: `curl http://localhost:3000/api/admin/points/mixed-orders/MIX-xxx -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
      {
        id: 'admin-mixed-retry-bbps',
        method: 'POST',
        path: '/api/admin/points/mixed-orders/:orderId/retry-bbps',
        title: 'Retry BBPS Payment',
        description: 'Retry a failed BBPS payment for an order where cash was already collected.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { adminUserId: 'admin_user' },
        responseExample: { success: true, message: 'BBPS payment retry initiated' },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/mixed-orders/MIX-xxx/retry-bbps \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"adminUserId": "admin_user"}'`,
      },
      {
        id: 'admin-mixed-refund',
        method: 'POST',
        path: '/api/admin/points/mixed-orders/:orderId/refund',
        title: 'Manual Refund',
        description: 'Manually trigger refund for a failed order. Releases locked points and initiates Razorpay refund.',
        auth: 'admin',
        pathParams: [{ name: 'orderId', type: 'string', required: true, description: 'Order ID' }],
        requestBody: { adminUserId: 'admin_user', reason: 'BBPS provider unavailable' },
        responseExample: { success: true, message: 'Refund initiated' },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/mixed-orders/MIX-xxx/refund \\
  -H "x-api-key: YOUR_ADMIN_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"adminUserId": "admin_user", "reason": "BBPS provider unavailable"}'`,
      },
      {
        id: 'admin-mixed-process-expired',
        method: 'POST',
        path: '/api/admin/points/mixed-orders/process-expired',
        title: 'Process Expired Orders',
        description: 'Manually trigger cleanup of expired payment-pending orders. Releases locked points.',
        auth: 'admin',
        responseExample: { success: true, data: { processed: 3, errors: 0 } },
        curlExample: `curl -X POST http://localhost:3000/api/admin/points/mixed-orders/process-expired -H "x-api-key: YOUR_ADMIN_KEY"`,
      },
    ],
  },
];

// Mobile App Integration Guide Content
const MOBILE_INTEGRATION_GUIDE = {
  title: 'Android App Integration Guide',
  subtitle: 'Step-by-step guide for integrating LinePe Points System in your Android app',
  sections: [
    {
      id: 'quickstart',
      title: '0. Quick Start & MVP Path',
      icon: '🚀',
      steps: [
        {
          title: 'Phased Integration Path',
          description: 'Follow this sequence for fastest go-live. Phase 1 is minimum for launch.',
          code: `┌─────────────────────────────────────────────────────────────────┐
│  PHASE 1: MVP (Go Live) - 2-3 days                              │
│  ─────────────────────────────────────────────────────────────  │
│  ✅ Setup & Auth     → Section 1 (required)                     │
│  ✅ User Balance     → Section 2 (show points in app)           │
│  ✅ Earning Preview  → Section 3 (show "You'll earn X points")  │
│  ✅ Error Handling   → Section 8 (basic error display)          │
└─────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────┐
│  PHASE 2: Redemption (Post-Launch) - 3-4 days                   │
│  ─────────────────────────────────────────────────────────────  │
│  ⏳ Browse Catalog   → Section 4 (categories, operators, plans) │
│  ⏳ Redeem Points    → Section 5 (initiate recharge/bill pay)   │
│  ⏳ Track Orders     → Section 6 (order status polling)         │
└─────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────┐
│  PHASE 3: Complete Experience - 1-2 days                        │
│  ─────────────────────────────────────────────────────────────  │
│  ⏳ Transaction History → Section 7 (points history list)       │
│  ⏳ Enhanced Error UX   → Section 8 (retry logic, offline)      │
└─────────────────────────────────────────────────────────────────┘`,
          language: 'text',
        },
        {
          title: 'API Endpoints Quick Reference',
          description: 'All endpoints at a glance. Base URL: https://content.linepe.in',
          code: `┌──────────────────────────────────────────────────────────────────────────────┐
│ PHASE 1: MVP ENDPOINTS (Required for Launch)                                 │
├────────┬─────────────────────────────────┬───────────────────────────────────┤
│ Method │ Endpoint                        │ Purpose                           │
├────────┼─────────────────────────────────┼───────────────────────────────────┤
│ GET    │ /api/v1/points/balance/{userId} │ Get user points balance           │
│ POST   │ /api/v1/points/earn/preview     │ Preview points before purchase    │
└────────┴─────────────────────────────────┴───────────────────────────────────┘

┌──────────────────────────────────────────────────────────────────────────────┐
│ PHASE 2: REDEMPTION ENDPOINTS                                                │
├────────┬─────────────────────────────────────────┬───────────────────────────┤
│ Method │ Endpoint                                │ Purpose                   │
├────────┼─────────────────────────────────────────┼───────────────────────────┤
│ GET    │ /api/v1/points/catalog/categories       │ List recharge categories  │
│ GET    │ /api/v1/points/catalog/operators?cat=X  │ Operators in category     │
│ GET    │ /api/v1/points/catalog/plans?op=X       │ Plans for operator        │
│ POST   │ /api/v1/points/redemption/preview       │ Calculate points/cash     │
│ POST   │ /api/v1/points/redemption/initiate      │ Execute redemption        │
│ GET    │ /api/v1/points/orders/{orderId}         │ Check order status        │
└────────┴─────────────────────────────────────────┴───────────────────────────┘

┌──────────────────────────────────────────────────────────────────────────────┐
│ PHASE 3: HISTORY ENDPOINT                                                    │
├────────┬─────────────────────────────────────────┬───────────────────────────┤
│ GET    │ /api/v1/points/transactions/{userId}    │ Transaction history       │
└────────┴─────────────────────────────────────────┴───────────────────────────┘`,
          language: 'text',
        },
        {
          title: 'Go-Live Checklist',
          description: 'Minimum requirements to launch your app with Points integration',
          code: `// GO-LIVE CHECKLIST for Android App

□ 1. API Key configured in app (x-api-key header)
□ 2. Base URL set to production: https://content.linepe.in
□ 3. Balance screen shows user points (GET /balance/{userId})
□ 4. Checkout screen shows earning preview (POST /earn/preview)
□ 5. Welcome bonus banner shown for first-time users (isFirstInvoice flag)
□ 6. Error handling displays user-friendly messages
□ 7. Network timeout set (recommended: 30 seconds)
□ 8. Tested with real user IDs in staging environment

// OPTIONAL BUT RECOMMENDED
□ Points balance caching (refresh on pull-to-refresh)
□ Offline state handling
□ Analytics events for points display/earning`,
          language: 'kotlin',
        },
        {
          title: 'Common Data Models',
          description: 'Reusable Kotlin data classes for API responses',
          code: `// ============ BASE RESPONSE WRAPPER ============
data class ApiResponse<T>(
    val success: Boolean,
    val data: T?,
    val error: ApiError?
)

data class ApiError(
    val code: String,
    val message: String
)

// ============ POINTS BALANCE ============
data class PointsBalance(
    val totalPoints: Int,
    val availablePoints: Int,
    val lockedPoints: Int,
    val expiringIn30Days: Int,
    val isFirstInvoice: Boolean  // Welcome bonus eligible
)

// ============ EARNING PREVIEW ============
data class EarningPreview(
    val basePoints: Int,
    val welcomeBonus: Int,       // > 0 if first-time user
    val totalPoints: Int,
    val tierBreakdown: List<TierBreakdown>
) {
    val isFirstTimeBonus: Boolean get() = welcomeBonus > 0
}

data class TierBreakdown(
    val tierName: String,
    val amountInTier: Int,
    val pointsPerThousand: Int,
    val pointsEarned: Int
)

// ============ CATALOG ============
data class Category(
    val id: String,
    val displayName: String,
    val icon: String,
    val maxPointsPercentage: Int,
    val minCashPercentage: Int
)

data class Operator(
    val id: String,
    val displayName: String,
    val logo: String?,
    val categoryId: String
)

data class Plan(
    val id: String,
    val amount: Int,
    val description: String,
    val validity: String?,
    val type: String  // "data", "talktime", "combo"
)

// ============ REDEMPTION ============
data class RedemptionPreview(
    val amount: Int,
    val maxPointsAllowed: Int,
    val minCashRequired: Int,
    val pointsToUse: Int,
    val pointsValue: Int,
    val cashAmount: Int,
    val isValid: Boolean
)

data class Order(
    val id: String,
    val status: String,  // "pending" | "processing" | "completed" | "failed"
    val pointsUsed: Int,
    val transactionId: String?
)

// ============ TRANSACTION HISTORY ============
data class Transaction(
    val id: String,
    val type: String,    // "EARN" | "REDEEM" | "EXPIRE"
    val points: Int,
    val description: String,
    val createdAt: String,
    val balanceAfter: Int
)`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'setup',
      title: '1. Setup & Authentication',
      icon: '🔐',
      steps: [
        {
          title: 'Add API Base URL',
          description: 'Configure the API base URL in your app',
          code: `// In your Constants.kt or BuildConfig
const val API_BASE_URL = "https://content.linepe.in"

// For development
const val API_BASE_URL_DEV = "http://localhost:3000"`,
          language: 'kotlin',
        },
        {
          title: 'Add API Key Header',
          description: 'All API requests require an API key in the header',
          code: `// Using OkHttp Interceptor
class ApiKeyInterceptor(private val apiKey: String) : Interceptor {
    override fun intercept(chain: Interceptor.Chain): Response {
        val request = chain.request().newBuilder()
            .addHeader("x-api-key", apiKey)
            .addHeader("Content-Type", "application/json")
            .build()
        return chain.proceed(request)
    }
}

// Add to OkHttpClient
val client = OkHttpClient.Builder()
    .addInterceptor(ApiKeyInterceptor("your-mobile-api-key"))
    .build()`,
          language: 'kotlin',
        },
        {
          title: 'Create Retrofit Service',
          description: 'Define the API interface using Retrofit',
          code: `interface LinePePointsApi {
    @GET("api/v1/points/balance/{userId}")
    suspend fun getBalance(@Path("userId") userId: String): BalanceResponse
    
    @POST("api/v1/points/earn/preview")
    suspend fun previewEarning(@Body request: EarnPreviewRequest): EarnPreviewResponse
    
    @GET("api/v1/points/catalog/categories")
    suspend fun getCategories(): CategoriesResponse
    
    @POST("api/v1/points/redemption/preview")
    suspend fun previewRedemption(@Body request: RedemptionPreviewRequest): RedemptionPreviewResponse
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'balance',
      title: '2. Display User Balance',
      icon: '💰',
      steps: [
        {
          title: 'Get User Balance',
          description: 'Fetch balance including welcome bonus eligibility (isFirstInvoice flag)',
          code: `// API Call
suspend fun getUserBalance(userId: String): PointsBalance {
    val response = api.getBalance(userId)
    return PointsBalance(
        total = response.data.totalPoints,
        available = response.data.availablePoints,
        locked = response.data.lockedPoints,
        expiringIn30Days = response.data.expiringIn30Days,
        // KEY FLAG: true = user eligible for Welcome Bonus on first purchase
        isEligibleForWelcomeBonus = response.data.isFirstInvoice
    )
}

// Data Class
data class PointsBalance(
    val total: Int,
    val available: Int,
    val locked: Int,
    val expiringIn30Days: Int,
    val isEligibleForWelcomeBonus: Boolean  // Show welcome bonus promo if true
)`,
          language: 'kotlin',
        },
        {
          title: 'Balance UI Component',
          description: 'Display balance with welcome bonus banner for first-time users',
          code: `@Composable
fun PointsBalanceCard(balance: PointsBalance) {
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Welcome Bonus Banner for first-time users
            if (balance.isEligibleForWelcomeBonus) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color(0xFFFFF3E0))
                        .padding(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("🎁", fontSize = 20.sp)
                    Spacer(Modifier.width(8.dp))
                    Column {
                        Text(
                            "Welcome Bonus Available!",
                            fontWeight = FontWeight.Bold,
                            color = Color(0xFFE65100)
                        )
                        Text(
                            "Make your first purchase to earn bonus points",
                            fontSize = 12.sp,
                            color = Color(0xFFE65100)
                        )
                    }
                }
                Spacer(Modifier.height(12.dp))
            }
            
            Text(
                text = "\${balance.available} Points",
                style = MaterialTheme.typography.headlineLarge,
                fontWeight = FontWeight.Bold
            )
            Text(text = "Available to redeem", color = Color.Gray)
            
            if (balance.expiringIn30Days > 0) {
                Text(
                    text = "⚠️ \${balance.expiringIn30Days} points expiring soon",
                    color = Color.Red,
                    fontSize = 12.sp
                )
            }
        }
    }
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'earning',
      title: '3. Earning Points Preview',
      icon: '⭐',
      steps: [
        {
          title: 'Preview Points on Invoice',
          description: 'Show users how many points they will earn. Pass includeWelcomeBonus: true to include welcome bonus for eligible users.',
          code: `// Request - Pass includeWelcomeBonus to request welcome bonus
data class EarnPreviewRequest(
    val userId: String,              // Required to check eligibility
    val invoiceAmount: Int,          // Amount in paise (e.g., 50000 = ₹500)
    val includeWelcomeBonus: Boolean // true = request welcome bonus if eligible
)

// Response Data Class
data class EarningPreview(
    val basePoints: Int,
    val welcomeBonus: Int,               // > 0 if eligible AND requested
    val welcomeBonusRequested: Boolean,  // What you requested
    val welcomeBonusEligible: Boolean,   // Whether user can get it
    val totalPoints: Int,
    val tierBreakdown: List<TierBreakdown>
) {
    val isFirstTimeBonus: Boolean get() = welcomeBonus > 0
}

// API Call - Request welcome bonus for first-time users
suspend fun previewEarning(
    userId: String, 
    amount: Int, 
    isFirstTimeUser: Boolean  // Your app knows if this is first purchase
): EarningPreview {
    val response = api.previewEarning(
        EarnPreviewRequest(
            userId = userId, 
            invoiceAmount = amount,
            includeWelcomeBonus = isFirstTimeUser  // Pass true for first-timers
        )
    )
    return EarningPreview(
        basePoints = response.data.basePoints,
        welcomeBonus = response.data.welcomeBonus,
        welcomeBonusRequested = response.data.welcomeBonusRequested,
        welcomeBonusEligible = response.data.welcomeBonusEligible,
        totalPoints = response.data.totalPoints,
        tierBreakdown = response.data.tierBreakdown
    )
}

// KEY: 
// - welcomeBonusRequested = what you asked for
// - welcomeBonusEligible = whether user can get it (first time only)
// - welcomeBonus > 0 = bonus will be included`,
          language: 'kotlin',
        },
        {
          title: 'Display Earning Preview with Welcome Bonus',
          description: 'Show breakdown with highlighted welcome bonus for first-time users',
          code: `@Composable
fun EarningPreviewCard(preview: EarningPreview) {
    Card {
        Column(modifier = Modifier.padding(16.dp)) {
            Text("You'll earn:", fontWeight = FontWeight.Bold)
            Spacer(modifier = Modifier.height(8.dp))
            
            // Base points
            Row(Modifier.fillMaxWidth()) {
                Text("Base Points")
                Spacer(Modifier.weight(1f))
                Text("+\${preview.basePoints}", color = Color.Green)
            }
            
            // Welcome Bonus - shown only for first-time users
            // KEY: Check if welcomeBonus > 0 to show this section
            if (preview.isFirstTimeBonus) {
                Spacer(Modifier.height(8.dp))
                Row(
                    Modifier
                        .fillMaxWidth()
                        .background(Color(0xFFFFF8E1))
                        .padding(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("🎁", fontSize = 18.sp)
                    Spacer(Modifier.width(8.dp))
                    Text(
                        "First Purchase Bonus!",
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFFFF6F00)
                    )
                    Spacer(Modifier.weight(1f))
                    Text(
                        "+\${preview.welcomeBonus}",
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFFFF6F00)
                    )
                }
            }
            
            Divider(modifier = Modifier.padding(vertical = 8.dp))
            
            Row(Modifier.fillMaxWidth()) {
                Text("Total", fontWeight = FontWeight.Bold)
                Spacer(Modifier.weight(1f))
                Text("\${preview.totalPoints} pts", fontWeight = FontWeight.Bold)
            }
        }
    }
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'catalog',
      title: '4. Browse Redemption Catalog',
      icon: '📦',
      steps: [
        {
          title: 'Fetch Categories',
          description: 'Get available redemption categories (Mobile, DTH, Bills, etc.)',
          code: `// API: GET /api/v1/points/catalog/categories
suspend fun getCategories(): List<Category> {
    val response = api.getCategories()
    return response.data.categories.map { cat ->
        Category(
            id = cat.id,
            name = cat.displayName,
            icon = mapIconToEmoji(cat.icon),
            maxPointsPercent = cat.maxPointsPercentage,
            minCashPercent = cat.minCashPercentage
        )
    }
}

// Note: maxPointsPercentage indicates how much can be paid with points
// e.g., 70% means user must pay at least 30% in cash`,
          language: 'kotlin',
        },
        {
          title: 'Fetch Operators in Category',
          description: 'Get operators/billers for a specific category',
          code: `// API: GET /api/v1/points/catalog/categories/{categoryId}/operators
suspend fun getOperators(categoryId: String): List<Operator> {
    val response = api.getOperatorsByCategory(categoryId)
    return response.data.operators.map { op ->
        Operator(
            id = op.id,
            name = op.displayName,
            logo = op.logo,
            categoryId = op.categoryId,
            hasPromotion = op.hasPromotion,
            promotionalBadge = op.promotionalBadge
        )
    }
}`,
          language: 'kotlin',
        },
        {
          title: 'Fetch Plans for Operator',
          description: 'Get recharge plans for mobile/DTH operators',
          code: `// API: GET /api/v1/points/catalog/operators/{operatorId}/plans
suspend fun getPlans(
    operatorId: String, 
    circle: String? = null
): List<Plan> {
    val response = api.getPlans(operatorId, circle)
    return response.data.plans.map { plan ->
        Plan(
            id = plan.id,
            amount = plan.amount,
            description = plan.description,
            validity = plan.validity,
            talktime = plan.talktime,
            data = plan.data,
            type = plan.type  // "data", "talktime", "combo"
        )
    }
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'redemption',
      title: '5. Redeem Points',
      icon: '🎁',
      steps: [
        {
          title: 'Preview Redemption',
          description: 'Calculate points/cash split before redemption',
          code: `// Request
data class RedemptionPreviewRequest(
    val userId: String,
    val categoryId: String,
    val amount: Int,        // Total amount in paise
    val pointsToUse: Int    // Points user wants to use
)

// API: POST /api/v1/points/redemption/preview
suspend fun previewRedemption(
    userId: String,
    categoryId: String,
    amount: Int,
    pointsToUse: Int
): RedemptionPreview {
    val response = api.previewRedemption(
        RedemptionPreviewRequest(userId, categoryId, amount, pointsToUse)
    )
    return RedemptionPreview(
        amount = response.data.amount,
        maxPointsAllowed = response.data.maxPointsAllowed,
        minCashRequired = response.data.minCashRequired,
        pointsToUse = response.data.pointsToUse,
        pointsValue = response.data.pointsValue,  // ₹ value of points
        cashAmount = response.data.cashAmount,     // Remaining cash to pay
        isValid = response.data.isValid,
        errors = response.data.validationErrors
    )
}`,
          language: 'kotlin',
        },
        {
          title: 'Points Slider UI',
          description: 'Let users choose how many points to use with a slider',
          code: `@Composable
fun PointsSlider(
    preview: RedemptionPreview,
    onPointsChanged: (Int) -> Unit
) {
    var sliderValue by remember { mutableStateOf(preview.pointsToUse.toFloat()) }
    
    Column {
        // Amount breakdown
        Row(Modifier.fillMaxWidth()) {
            Text("Total: ₹\${preview.amount / 100}")
            Spacer(Modifier.weight(1f))
            Column(horizontalAlignment = Alignment.End) {
                Text("Points: ₹\${preview.pointsValue}", color = Color.Green)
                Text("Cash: ₹\${preview.cashAmount}", color = Color.Blue)
            }
        }
        
        Spacer(modifier = Modifier.height(16.dp))
        
        // Slider
        Text("Use \${sliderValue.toInt()} points")
        Slider(
            value = sliderValue,
            onValueChange = { 
                sliderValue = it
                onPointsChanged(it.toInt())
            },
            valueRange = 0f..preview.maxPointsAllowed.toFloat(),
            steps = 10
        )
        
        // Min cash warning
        Text(
            "Minimum cash required: ₹\${preview.minCashRequired}",
            fontSize = 12.sp,
            color = Color.Gray
        )
    }
}`,
          language: 'kotlin',
        },
        {
          title: 'Initiate Redemption',
          description: 'Execute the redemption (recharge/bill payment)',
          code: `// Request
data class InitiateRedemptionRequest(
    val userId: String,
    val categoryId: String,
    val operatorId: String,
    val amount: Int,
    val pointsToUse: Int,
    val serviceDetails: ServiceDetails
)

data class ServiceDetails(
    val mobileNumber: String? = null,
    val accountNumber: String? = null,
    val planId: String? = null
)

// API: POST /api/v1/points/redemption/initiate
suspend fun initiateRedemption(request: InitiateRedemptionRequest): Order {
    val response = api.initiateRedemption(request)
    return Order(
        orderId = response.data.order.id,
        status = response.data.order.status,  // "pending" | "processing" | "completed" | "failed"
        pointsDeducted = response.data.order.pointsUsed,
        transactionId = response.data.order.transactionId
    )
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'status',
      title: '6. Track Order Status',
      icon: '📋',
      steps: [
        {
          title: 'Check Order Status',
          description: 'Poll for order completion status',
          code: `// API: GET /api/v1/points/orders/{orderId}
suspend fun getOrderStatus(orderId: String): OrderStatus {
    val response = api.getOrder(orderId)
    return OrderStatus(
        orderId = response.data.id,
        status = response.data.status,
        statusMessage = getStatusMessage(response.data.status),
        transactionId = response.data.pgTransactionId,
        completedAt = response.data.completedAt
    )
}

fun getStatusMessage(status: String): String = when(status) {
    "pending" -> "Order received, processing..."
    "processing" -> "Payment in progress..."
    "completed" -> "Recharge successful! ✅"
    "failed" -> "Transaction failed. Points refunded."
    else -> "Unknown status"
}`,
          language: 'kotlin',
        },
        {
          title: 'Status Polling with Coroutines',
          description: 'Poll order status until completion',
          code: `suspend fun pollOrderStatus(
    orderId: String,
    onStatusUpdate: (OrderStatus) -> Unit
) {
    var attempts = 0
    val maxAttempts = 30  // 30 * 2s = 60s max
    
    while (attempts < maxAttempts) {
        val status = getOrderStatus(orderId)
        onStatusUpdate(status)
        
        when (status.status) {
            "completed", "failed" -> break
            else -> {
                delay(2000)  // Poll every 2 seconds
                attempts++
            }
        }
    }
}

// Usage in ViewModel
viewModelScope.launch {
    pollOrderStatus(orderId) { status ->
        _orderStatus.value = status
    }
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'history',
      title: '7. Transaction History',
      icon: '📜',
      steps: [
        {
          title: 'Get Transaction History',
          description: 'Fetch user\'s points transaction history',
          code: `// API: GET /api/v1/points/transactions/{userId}?limit=50&offset=0
suspend fun getTransactionHistory(
    userId: String,
    limit: Int = 50,
    offset: Int = 0
): List<Transaction> {
    val response = api.getTransactions(userId, limit, offset)
    return response.data.transactions.map { txn ->
        Transaction(
            id = txn.id,
            type = txn.type,  // "EARN" | "REDEEM" | "EXPIRE" | "ADJUST"
            points = txn.points,
            description = txn.description,
            createdAt = txn.createdAt,
            balanceAfter = txn.balanceAfter
        )
    }
}`,
          language: 'kotlin',
        },
        {
          title: 'Transaction List UI',
          description: 'Display transaction history with icons',
          code: `@Composable
fun TransactionItem(txn: Transaction) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Icon
        val (icon, color) = when(txn.type) {
            "EARN" -> "💰" to Color.Green
            "REDEEM" -> "🎁" to Color.Blue
            "EXPIRE" -> "⏰" to Color.Red
            else -> "📝" to Color.Gray
        }
        Text(icon, fontSize = 24.sp)
        
        Spacer(modifier = Modifier.width(16.dp))
        
        // Details
        Column(modifier = Modifier.weight(1f)) {
            Text(txn.description, fontWeight = FontWeight.Medium)
            Text(
                formatDate(txn.createdAt),
                fontSize = 12.sp,
                color = Color.Gray
            )
        }
        
        // Points
        val prefix = if (txn.points > 0) "+" else ""
        Text(
            "\$prefix\${txn.points}",
            color = color,
            fontWeight = FontWeight.Bold
        )
    }
}`,
          language: 'kotlin',
        },
      ],
    },
    {
      id: 'errors',
      title: '8. Error Handling',
      icon: '⚠️',
      steps: [
        {
          title: 'API Error Response Format',
          description: 'All API errors follow this format',
          code: `// Error Response Structure
{
  "success": false,
  "error": {
    "code": "INSUFFICIENT_POINTS",
    "message": "User does not have enough points for this redemption"
  }
}

// Common Error Codes
// - UNAUTHORIZED: Invalid or missing API key
// - USER_NOT_FOUND: User ID doesn't exist
// - INSUFFICIENT_POINTS: Not enough points for redemption
// - INVALID_AMOUNT: Amount out of valid range
// - OPERATOR_UNAVAILABLE: Selected operator is not active
// - RATE_LIMITED: Too many requests, try again later`,
          language: 'json',
        },
        {
          title: 'Error Handling in Kotlin',
          description: 'Handle API errors gracefully',
          code: `sealed class ApiResult<out T> {
    data class Success<T>(val data: T) : ApiResult<T>()
    data class Error(val code: String, val message: String) : ApiResult<Nothing>()
}

suspend fun <T> safeApiCall(call: suspend () -> T): ApiResult<T> {
    return try {
        ApiResult.Success(call())
    } catch (e: HttpException) {
        val errorBody = e.response()?.errorBody()?.string()
        val error = parseError(errorBody)
        ApiResult.Error(error.code, error.message)
    } catch (e: IOException) {
        ApiResult.Error("NETWORK_ERROR", "Please check your internet connection")
    }
}

// Usage
when (val result = safeApiCall { api.getBalance(userId) }) {
    is ApiResult.Success -> showBalance(result.data)
    is ApiResult.Error -> showError(result.message)
}`,
          language: 'kotlin',
        },
      ],
    },
  ],
};

// System Architecture Content
const ARCHITECTURE_CONTENT = {
  title: 'LinePe Points System Architecture',
  subtitle: 'Complete system architecture overview for the Points Earning, Redemption & BBPS Integration',
  sections: [
    {
      id: 'overview',
      title: 'System Overview',
      icon: '🏗️',
      content: `
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│                           LINEPE POINTS SYSTEM - COMPLETE ARCHITECTURE                       │
│                                     (Phases 1-16 Complete)                                   │
├─────────────────────────────────────────────────────────────────────────────────────────────┤
│                                                                                              │
│   ┌──────────────┐     ┌──────────────┐     ┌──────────────┐     ┌──────────────┐          │
│   │  Mobile App  │     │  Admin Portal│     │  External    │     │   Webhooks   │          │
│   │  (Android)   │     │   (React)    │     │   Systems    │     │  (Razorpay)  │          │
│   └──────┬───────┘     └──────┬───────┘     └──────┬───────┘     └──────┬───────┘          │
│          │                    │                    │                    │                   │
│          │ x-api-key          │ x-api-key          │ x-api-key          │ signature         │
│          │ (mobile)           │ (admin)            │ (internal)         │                   │
│          ▼                    ▼                    ▼                    ▼                   │
│   ┌─────────────────────────────────────────────────────────────────────────────────┐      │
│   │                              API GATEWAY (Express.js)                            │      │
│   │  ┌─────────────┬─────────────┬─────────────┬─────────────┬─────────────────────┐│      │
│   │  │Authentication│Rate Limiting│ Request    │  Logging   │  Error Handling     ││      │
│   │  │ (API Key)   │  Middleware │ Validation │             │                     ││      │
│   │  └─────────────┴─────────────┴─────────────┴─────────────┴─────────────────────┘│      │
│   └────────────────────────────────────┬────────────────────────────────────────────┘      │
│                                        │                                                    │
│   ┌────────────────────────────────────┼────────────────────────────────────────────┐      │
│   │                           ROUTE LAYER                                            │      │
│   │  ┌─────────────┐ ┌─────────────┐ ┌─────────────┐ ┌─────────────┐ ┌────────────┐ │      │
│   │  │   /earn     │ │  /balance   │ │ /redemption │ │  /catalog   │ │  /payment  │ │      │
│   │  │   /earn/    │ │  /balance/  │ │ /redemption/│ │  /catalog/  │ │  /webhook/ │ │      │
│   │  │   preview   │ │  /{userId}  │ │ initiate-   │ │ categories  │ │  razorpay  │ │      │
│   │  │             │ │             │ │ mixed       │ │             │ │            │ │      │
│   │  └──────┬──────┘ └──────┬──────┘ └──────┬──────┘ └──────┬──────┘ └─────┬──────┘ │      │
│   └─────────┼───────────────┼───────────────┼───────────────┼──────────────┼────────┘      │
│             │               │               │               │              │               │
│   ┌─────────┴───────────────┴───────────────┴───────────────┴──────────────┴────────┐      │
│   │                              SERVICE LAYER                                       │      │
│   │                                                                                  │      │
│   │  ┌──────────────┐  ┌──────────────┐  ┌──────────────┐  ┌──────────────────────┐ │      │
│   │  │ EarningService│  │BalanceService│  │Redemption   │  │ MixedRedemption      │ │      │
│   │  │ • calculatePts│  │ • getBalance │  │Service      │  │ Service (Phase 16)   │ │      │
│   │  │ • earnPoints │  │ • lockPoints │  │ • preview   │  │ • initiateMixed      │ │      │
│   │  │ • preview    │  │ • release    │  │ • initiate  │  │ • handlePayment      │ │      │
│   │  │              │  │ • deduct     │  │ • complete  │  │ • executeBBPS        │ │      │
│   │  └──────────────┘  └──────────────┘  └──────────────┘  └──────────────────────┘ │      │
│   │                                                                                  │      │
│   │  ┌──────────────┐  ┌──────────────┐  ┌──────────────┐  ┌──────────────────────┐ │      │
│   │  │CatalogService│  │ConfigService │  │ ExpiryService│  │PaymentOrderService   │ │      │
│   │  │ • categories │  │ • earning    │  │ • FIFO expiry│  │ • createOrder        │ │      │
│   │  │ • operators  │  │ • expiry     │  │ • batch job │  │ • verifySignature    │ │      │
│   │  │ • plans      │  │ • redemption │  │ • cleanup   │  │ • initiateRefund     │ │      │
│   │  └──────────────┘  └──────────────┘  └──────────────┘  └──────────────────────┘ │      │
│   │                                                                                  │      │
│   │  ┌───────────────────────────────────────────────────────────────────────────┐  │      │
│   │  │                    BBPS GATEWAY ABSTRACTION (Phase 11-15)                 │  │      │
│   │  │  ┌────────────────────────────────────────────────────────────────────┐   │  │      │
│   │  │  │                    IBBPSGatewayAdapter (Interface)                 │   │  │      │
│   │  │  │  • getCategories()  • getBillers()  • fetchBill()  • payBill()     │   │  │      │
│   │  │  │  • getPlans()       • detectOperator()  • executePrepaidRecharge() │   │  │      │
│   │  │  └────────────────────────────────────────────────────────────────────┘   │  │      │
│   │  │                              ▲                    ▲                       │  │      │
│   │  │              ┌───────────────┴──────┐   ┌────────┴───────────────┐        │  │      │
│   │  │              │   PayUBBPSAdapter    │   │  RazorpayBBPSAdapter   │        │  │      │
│   │  │              │   (OAuth 2.0)        │   │  (Basic Auth)          │        │  │      │
│   │  │              └──────────────────────┘   └────────────────────────┘        │  │      │
│   │  │                                                                           │  │      │
│   │  │  ┌──────────────────┐  ┌──────────────────┐  ┌──────────────────────────┐ │  │      │
│   │  │  │BBPSGatewayFactory│  │BBPSCircuitBreaker│  │BBPSRetryHandler         │ │  │      │
│   │  │  │ • getAdapter()   │  │ • open/close    │  │ • exponential backoff   │ │  │      │
│   │  │  │ • failover       │  │ • half-open     │  │ • max retries: 3        │ │  │      │
│   │  │  └──────────────────┘  └──────────────────┘  └──────────────────────────┘ │  │      │
│   │  └───────────────────────────────────────────────────────────────────────────┘  │      │
│   └─────────────────────────────────────────────────────────────────────────────────┘      │
│                                        │                                                    │
│   ┌────────────────────────────────────┼────────────────────────────────────────────┐      │
│   │                              DATA LAYER                                          │      │
│   │                                                                                  │      │
│   │  ┌────────────────────────────────────────────────────────────────────────────┐ │      │
│   │  │                    PointsCosmosService (Azure Cosmos DB)                   │ │      │
│   │  │                                                                            │ │      │
│   │  │  Container: linepe-points                                                  │ │      │
│   │  │  Partition Key: partitionKey (user_{userId}, config, etc.)                 │ │      │
│   │  │                                                                            │ │      │
│   │  │  ┌──────────────┐ ┌──────────────┐ ┌──────────────┐ ┌──────────────┐      │ │      │
│   │  │  │ user_balance │ │ transaction  │ │redemption_   │ │   config     │      │ │      │
│   │  │  │ • totalPoints│ │ • EARN/REDEEM│ │    order     │ │ • earning    │      │ │      │
│   │  │  │ • buckets[]  │ │ • points     │ │ • status     │ │ • expiry     │      │ │      │
│   │  │  │ • lifetime   │ │ • source     │ │ • mixedPmt   │ │ • redemption │      │ │      │
│   │  │  └──────────────┘ └──────────────┘ └──────────────┘ └──────────────┘      │ │      │
│   │  │  ┌──────────────┐ ┌──────────────┐ ┌──────────────┐ ┌──────────────┐      │ │      │
│   │  │  │catalog_      │ │catalog_      │ │ catalog_plan │ │    audit     │      │ │      │
│   │  │  │  category    │ │  operator    │ │ (cached)     │ │ • action     │      │ │      │
│   │  │  └──────────────┘ └──────────────┘ └──────────────┘ └──────────────┘      │ │      │
│   │  └────────────────────────────────────────────────────────────────────────────┘ │      │
│   └─────────────────────────────────────────────────────────────────────────────────┘      │
│                                                                                              │
│   ┌─────────────────────────────────────────────────────────────────────────────────┐      │
│   │                           EXTERNAL INTEGRATIONS                                  │      │
│   │                                                                                  │      │
│   │  ┌────────────────┐    ┌────────────────┐    ┌────────────────────────────────┐ │      │
│   │  │    Razorpay    │    │     PayU       │    │         Azure Services         │ │      │
│   │  │                │    │                │    │                                │ │      │
│   │  │  • Orders API  │    │  • OAuth Token │    │  • Cosmos DB (data)            │ │      │
│   │  │  • Payments    │    │  • BBPS NBC    │    │  • Blob Storage (assets)       │ │      │
│   │  │  • Refunds     │    │  • Categories  │    │  • Key Vault (secrets)         │ │      │
│   │  │  • Webhooks    │    │  • Billers     │    │  • App Service (hosting)       │ │      │
│   │  └────────────────┘    └────────────────┘    └────────────────────────────────┘ │      │
│   └─────────────────────────────────────────────────────────────────────────────────┘      │
│                                                                                              │
└─────────────────────────────────────────────────────────────────────────────────────────────┘
`,
    },
    {
      id: 'data-flow',
      title: 'Data Flow Diagrams',
      icon: '🔄',
      content: `
┌─────────────────────────────────────────────────────────────────────────────────┐
│                         POINTS EARNING FLOW                                      │
├─────────────────────────────────────────────────────────────────────────────────┤
│                                                                                  │
│   Mobile App                    Backend                         Cosmos DB       │
│       │                            │                                │           │
│       │  POST /earn                │                                │           │
│       │  { userId, amount,         │                                │           │
│       │    includeWelcomeBonus }   │                                │           │
│       │ ─────────────────────────► │                                │           │
│       │                            │  getOrCreateUserBalance()      │           │
│       │                            │ ─────────────────────────────► │           │
│       │                            │ ◄───────────────────────────── │           │
│       │                            │                                │           │
│       │                            │  calculatePoints()             │           │
│       │                            │  ├── getTiers()                │           │
│       │                            │  ├── applyWelcomeBonus()       │           │
│       │                            │  └── checkLimits()             │           │
│       │                            │                                │           │
│       │                            │  createTransaction()           │           │
│       │                            │ ─────────────────────────────► │           │
│       │                            │  updateBalance()               │           │
│       │                            │ ─────────────────────────────► │           │
│       │                            │                                │           │
│       │  { pointsEarned,           │                                │           │
│       │    welcomeBonus,           │                                │           │
│       │    newBalance }            │                                │           │
│       │ ◄───────────────────────── │                                │           │
│       │                            │                                │           │
└─────────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────────┐
│                    MIXED REDEMPTION FLOW (Phase 16)                              │
├─────────────────────────────────────────────────────────────────────────────────┤
│                                                                                  │
│   Mobile App          Backend           Razorpay          BBPS           DB     │
│       │                  │                  │               │             │     │
│       │ initiate-mixed   │                  │               │             │     │
│       │ ───────────────► │                  │               │             │     │
│       │                  │  lockPoints()    │               │             │     │
│       │                  │ ─────────────────────────────────────────────► │     │
│       │                  │                  │               │             │     │
│       │                  │  createOrder()   │               │             │     │
│       │                  │ ───────────────► │               │             │     │
│       │                  │ ◄─────────────── │               │             │     │
│       │                  │                  │               │             │     │
│       │ { razorpayOrderId,                  │               │             │     │
│       │   amountInPaise }│                  │               │             │     │
│       │ ◄─────────────── │                  │               │             │     │
│       │                  │                  │               │             │     │
│       │  RAZORPAY CHECKOUT                  │               │             │     │
│       │ ═══════════════════════════════════►│               │             │     │
│       │                  │                  │               │             │     │
│       │ payment success  │                  │               │             │     │
│       │ ◄════════════════════════════════════               │             │     │
│       │                  │                  │               │             │     │
│       │ confirm-payment  │                  │               │             │     │
│       │ ───────────────► │                  │               │             │     │
│       │                  │  verifySignature()               │             │     │
│       │                  │ ───────────────► │               │             │     │
│       │                  │ ◄─────────────── │               │             │     │
│       │                  │                  │               │             │     │
│       │                  │  executeBBPSPayment()            │             │     │
│       │                  │ ───────────────────────────────► │             │     │
│       │                  │ ◄─────────────────────────────── │             │     │
│       │                  │                  │               │             │     │
│       │                  │  deductLockedPoints()            │             │     │
│       │                  │ ─────────────────────────────────────────────► │     │
│       │                  │  createTransaction()             │             │     │
│       │                  │ ─────────────────────────────────────────────► │     │
│       │                  │                  │               │             │     │
│       │ { status: completed }               │               │             │     │
│       │ ◄─────────────── │                  │               │             │     │
│       │                  │                  │               │             │     │
└─────────────────────────────────────────────────────────────────────────────────┘
`,
    },
    {
      id: 'components',
      title: 'Component Details',
      icon: '📦',
      content: `
┌─────────────────────────────────────────────────────────────────────────────────┐
│                            SERVICE COMPONENTS                                    │
├─────────────────────────────────────────────────────────────────────────────────┤
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ EarningService                                                             │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Purpose: Calculate and award points based on invoice amounts               │  │
│  │                                                                            │  │
│  │ Key Methods:                                                               │  │
│  │   • calculatePoints(amount, userId, isFirst, includeWelcome)               │  │
│  │   • earnPoints(request) → Creates transaction, updates balance             │  │
│  │   • previewEarning(amount, userId, includeWelcome)                         │  │
│  │                                                                            │  │
│  │ Features:                                                                  │  │
│  │   ✓ Tiered earning (50/25/10 pts per ₹1000)                               │  │
│  │   ✓ Welcome bonus for first invoice                                       │  │
│  │   ✓ Bonus multiplier for promotions                                       │  │
│  │   ✓ Per-invoice/day/month limits                                          │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ BalanceService                                                             │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Purpose: Manage user points balances with FIFO bucket tracking             │  │
│  │                                                                            │  │
│  │ Key Methods:                                                               │  │
│  │   • getBalance(userId) → Full balance with expiry breakdown                │  │
│  │   • lockPoints(userId, points, reason) → Lock for pending redemption       │  │
│  │   • releasePoints(userId, points, reason) → Release on cancel/failure      │  │
│  │   • deductLockedPoints(userId, points, reason) → Final deduction           │  │
│  │                                                                            │  │
│  │ Features:                                                                  │  │
│  │   ✓ FIFO bucket management (oldest points expire first)                    │  │
│  │   ✓ Expiry tracking (7/30/90 days)                                         │  │
│  │   ✓ Lifetime statistics                                                    │  │
│  │   ✓ Points locking for atomic operations                                   │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ MixedRedemptionService (Phase 16)                                          │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Purpose: Orchestrate points + cash redemption flow                         │  │
│  │                                                                            │  │
│  │ Key Methods:                                                               │  │
│  │   • previewMixedRedemption() → Calculate split                             │  │
│  │   • initiateMixedRedemption() → Lock points, create Razorpay order         │  │
│  │   • handlePaymentConfirmed() → Process after successful payment            │  │
│  │   • handlePaymentFailed() → Release points on failure                      │  │
│  │   • handleBBPSFailure() → Refund cash, release points                      │  │
│  │   • completeOrder() → Deduct points, update status                         │  │
│  │   • cancelOrder() → Release points, mark cancelled                         │  │
│  │                                                                            │  │
│  │ Order States:                                                              │  │
│  │   pending → payment_pending → processing → completed                       │  │
│  │                          ↓           ↓                                     │  │
│  │                    payment_failed  failed → refunded                       │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ BBPS Gateway Abstraction                                                   │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Purpose: Provider-agnostic BBPS operations (PayU / Razorpay)               │  │
│  │                                                                            │  │
│  │ Components:                                                                │  │
│  │   • IBBPSGatewayAdapter - Common interface for all providers               │  │
│  │   • BBPSGatewayFactory - Runtime adapter selection & failover              │  │
│  │   • PayUBBPSAdapter - PayU implementation (OAuth 2.0)                      │  │
│  │   • RazorpayBBPSAdapter - Razorpay implementation (Basic Auth)             │  │
│  │   • BBPSCircuitBreaker - Prevent cascading failures                        │  │
│  │   • BBPSRetryHandler - Exponential backoff retry                           │  │
│  │                                                                            │  │
│  │ Adapter Methods:                                                           │  │
│  │   • getCategories() → BBPS bill categories                                 │  │
│  │   • getBillersByCategory() → Billers in category                           │  │
│  │   • fetchBill() → Fetch bill details                                       │  │
│  │   • payBill() → Execute bill payment                                       │  │
│  │   • getOperators() → Mobile/DTH operators                                  │  │
│  │   • getPlans() → Recharge plans                                            │  │
│  │   • executePrepaidRecharge() → Mobile/DTH recharge                         │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
└─────────────────────────────────────────────────────────────────────────────────┘
`,
    },
    {
      id: 'database',
      title: 'Database Schema',
      icon: '🗄️',
      content: `
┌─────────────────────────────────────────────────────────────────────────────────┐
│                         COSMOS DB SCHEMA (linepe-points)                         │
├─────────────────────────────────────────────────────────────────────────────────┤
│                                                                                  │
│  Partition Strategy: partitionKey field                                          │
│    • user_{userId} - User-specific data (balance, transactions, orders)          │
│    • config - System configurations                                              │
│    • catalog - Catalog data                                                      │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ user_balance                                      partition: user_{userId} │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ {                                                                          │  │
│  │   "id": "bal_user123",                                                     │  │
│  │   "docType": "user_balance",                                               │  │
│  │   "userId": "user123",                                                     │  │
│  │   "totalPoints": 15000,                                                    │  │
│  │   "availablePoints": 12000,                                                │  │
│  │   "lockedPoints": 3000,                                                    │  │
│  │   "pointsBuckets": [                                                       │  │
│  │     { "id": "bucket_1", "points": 5000, "remainingPoints": 5000,          │  │
│  │       "type": "earned", "expiresAt": "2026-06-18T00:00:00Z" },            │  │
│  │     { "id": "bucket_2", "points": 10000, "remainingPoints": 7000,         │  │
│  │       "type": "welcome_bonus", "expiresAt": "2026-02-18T00:00:00Z" }      │  │
│  │   ],                                                                       │  │
│  │   "lifetime": { "earned": 50000, "redeemed": 35000, "expired": 0 },       │  │
│  │   "isFirstInvoice": false,                                                 │  │
│  │   "lastEarnedAt": "2026-01-15T10:30:00Z"                                  │  │
│  │ }                                                                          │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ transaction                                       partition: user_{userId} │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ {                                                                          │  │
│  │   "id": "txn_1705587600000",                                               │  │
│  │   "docType": "transaction",                                                │  │
│  │   "userId": "user123",                                                     │  │
│  │   "type": "EARN" | "REDEEM" | "EXPIRE" | "ADJUST" | "MIGRATE",            │  │
│  │   "points": 2500,  // positive = credit, negative = debit                  │  │
│  │   "balanceAfter": 15000,                                                   │  │
│  │   "source": {                                                              │  │
│  │     "type": "invoice" | "redemption" | "expiry_job" | "admin_adjust",     │  │
│  │     "id": "INV-001",                                                       │  │
│  │     "description": "Invoice payment"                                       │  │
│  │   },                                                                       │  │
│  │   "metadata": { "invoiceAmount": 50000, "welcomeBonus": true }            │  │
│  │ }                                                                          │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ redemption_order                                  partition: user_{userId} │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ {                                                                          │  │
│  │   "id": "MIX-1705587600000-abc123",                                        │  │
│  │   "docType": "redemption_order",                                           │  │
│  │   "userId": "user123",                                                     │  │
│  │   "status": "payment_pending" | "processing" | "completed" | "failed",    │  │
│  │   "amount": 500,                                                           │  │
│  │   "pointsUsed": 300,                                                       │  │
│  │   "pointsValue": 300,                                                      │  │
│  │   "cashAmount": 200,                                                       │  │
│  │   "isMixedRedemption": true,                                               │  │
│  │   "mixedPayment": {                                                        │  │
│  │     "razorpayOrderId": "order_abc123",                                     │  │
│  │     "razorpayPaymentId": "pay_xyz789",                                     │  │
│  │     "paymentStatus": "payment_captured",                                   │  │
│  │     "cashAmount": 200,                                                     │  │
│  │     "amountInPaise": 20000,                                                │  │
│  │     "paymentTimeoutAt": "2026-01-18T12:30:00Z"                            │  │
│  │   },                                                                       │  │
│  │   "serviceDetails": { "type": "electricity", "beneficiary": "123456" },   │  │
│  │   "fulfillment": { "status": "completed", "completedAt": "..." }          │  │
│  │ }                                                                          │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ config                                                  partition: config  │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Types: earning | expiry | redemption | payment_gateway | bbps_gateway     │  │
│  │                                                                            │  │
│  │ earning config:                                                            │  │
│  │   • isActive: boolean                                                      │  │
│  │   • tiers: [{ minAmount, maxAmount, pointsPerThousand }]                  │  │
│  │   • welcomeBonus: { enabled, points, minInvoiceAmount }                   │  │
│  │   • bonusMultiplier: { enabled, multiplier, startDate, endDate }          │  │
│  │   • limits: { maxPointsPerInvoice, maxPointsPerDay, maxPointsPerMonth }   │  │
│  │                                                                            │  │
│  │ bbps_gateway config:                                                       │  │
│  │   • activeProvider: "payu" | "razorpay" | "none"                          │  │
│  │   • fallbackProvider: "payu" | "razorpay" | "none"                        │  │
│  │   • payu: { enabled, environment, credentials, endpoints }                │  │
│  │   • razorpay: { enabled, environment, credentials }                       │  │
│  │   • catalogSync: { enabled, intervalHours, lastSyncAt }                   │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
└─────────────────────────────────────────────────────────────────────────────────┘
`,
    },
    {
      id: 'api-summary',
      title: 'API Summary',
      icon: '🔗',
      content: `
┌─────────────────────────────────────────────────────────────────────────────────┐
│                              API SUMMARY (89+ APIs)                              │
├─────────────────────────────────────────────────────────────────────────────────┤
│                                                                                  │
│  PUBLIC APIs (Mobile / Internal)                                                 │
│  ─────────────────────────────────────────────────────────────────────────────  │
│                                                                                  │
│  EARNING (3 APIs)                                                                │
│    POST /api/v1/points/earn              → Award points for invoice              │
│    POST /api/v1/points/earn/preview      → Preview potential earnings            │
│    GET  /api/v1/points/earn/tiers        → Get active earning tiers              │
│                                                                                  │
│  BALANCE (5 APIs)                                                                │
│    GET  /api/v1/points/balance/:userId   → Get user balance                      │
│    GET  /api/v1/points/balance/:userId/summary → Balance summary                 │
│    GET  /api/v1/points/balance/:userId/buckets → FIFO buckets                    │
│    GET  /api/v1/points/transactions/:userId    → Transaction history             │
│    GET  /api/v1/points/transactions/:userId/:id → Transaction detail             │
│                                                                                  │
│  REDEMPTION (6 APIs)                                                             │
│    POST /api/v1/points/redemption/preview      → Calculate split                 │
│    POST /api/v1/points/redemption/initiate     → Start redemption                │
│    POST /api/v1/points/redemption/:id/confirm  → Confirm order                   │
│    POST /api/v1/points/redemption/:id/cancel   → Cancel order                    │
│    GET  /api/v1/points/redemption/:id          → Order status                    │
│    GET  /api/v1/points/redemption/user/:userId → User orders                     │
│                                                                                  │
│  MIXED REDEMPTION - Phase 16 (6 APIs)                                            │
│    POST /api/v1/points/redemption/preview-mixed     → Preview mixed split        │
│    POST /api/v1/points/redemption/initiate-mixed    → Start mixed redemption     │
│    POST /api/v1/points/redemption/mixed-order/:id/confirm-payment → Confirm pay  │
│    GET  /api/v1/points/redemption/mixed-order/:id/status → Order status          │
│    POST /api/v1/points/redemption/mixed-order/:id/cancel → Cancel order          │
│    GET  /api/v1/points/redemption/mixed-orders/:userId   → User mixed orders     │
│                                                                                  │
│  CATALOG (6 APIs)                                                                │
│    GET  /api/v1/points/catalog             → Catalog overview                    │
│    GET  /api/v1/points/catalog/categories  → All categories                      │
│    GET  /api/v1/points/catalog/operators   → Operators in category               │
│    GET  /api/v1/points/catalog/plans       → Plans for operator                  │
│    POST /api/v1/points/catalog/validate    → Validate beneficiary                │
│    GET  /api/v1/points/catalog/search      → Search catalog                      │
│                                                                                  │
│  WEBHOOKS (3 APIs)                                                               │
│    POST /api/v1/points/payment/webhook          → Unified webhook                │
│    POST /api/v1/points/payment/webhook/razorpay → Razorpay webhook               │
│    POST /api/v1/points/payment/webhook/payu     → PayU webhook                   │
│                                                                                  │
│  ─────────────────────────────────────────────────────────────────────────────  │
│  ADMIN APIs (Admin Tier Only)                                                    │
│  ─────────────────────────────────────────────────────────────────────────────  │
│                                                                                  │
│  CONFIG (4 APIs)        → Get/update system configurations                       │
│  DASHBOARD (3 APIs)     → Stats, trends, activity                                │
│  USERS (7 APIs)         → Search, details, adjust, lock/unlock                   │
│  EXPIRY (4 APIs)        → Pending, process, stats, history                       │
│  CATALOG (9 APIs)       → CRUD categories, operators, sync                       │
│  REDEMPTION (4 APIs)    → Orders, details, refund, stats                         │
│  NOTIFICATIONS (5 APIs) → Templates, send, logs                                  │
│  MIGRATION (9 APIs)     → Batches, preview, execute, rollback                    │
│  AUDIT (3 APIs)         → Logs, export, search                                   │
│  BBPS (14 APIs)         → Config, health, switch provider, catalog sync          │
│  MIXED ORDERS (6 APIs)  → List, stats, details, retry, refund, process-expired   │
│                                                                                  │
│  ─────────────────────────────────────────────────────────────────────────────  │
│  Authentication: x-api-key header                                                │
│    • mobile tier  → Public APIs                                                  │
│    • internal tier → Public APIs + webhook processing                            │
│    • admin tier   → All APIs including admin endpoints                           │
│                                                                                  │
└─────────────────────────────────────────────────────────────────────────────────┘
`,
    },
    {
      id: 'phases',
      title: 'Implementation Phases',
      icon: '📅',
      content: `
┌─────────────────────────────────────────────────────────────────────────────────┐
│                         IMPLEMENTATION PHASES (1-16)                             │
├─────────────────────────────────────────────────────────────────────────────────┤
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ FOUNDATION (Phases 1-4)                                           ✅ Done  │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Phase 1: Core Infrastructure                                               │  │
│  │   • Cosmos DB setup, PointsCosmosService                                   │  │
│  │   • Basic configs (earning, expiry, redemption)                            │  │
│  │   • Admin portal structure                                                 │  │
│  │                                                                            │  │
│  │ Phase 2: Earning System                                                    │  │
│  │   • Tiered earning calculation (50/25/10 pts per ₹1K)                     │  │
│  │   • Welcome bonus, bonus multiplier                                        │  │
│  │   • Earning limits (per invoice/day/month)                                 │  │
│  │                                                                            │  │
│  │ Phase 3: Balance Management                                                │  │
│  │   • FIFO bucket tracking                                                   │  │
│  │   • Transaction history with pagination                                    │  │
│  │   • Admin user management                                                  │  │
│  │                                                                            │  │
│  │ Phase 4: Expiry System                                                     │  │
│  │   • FIFO expiry processing                                                 │  │
│  │   • Different expiry by point type                                         │  │
│  │   • Grace period, pre-expiry notifications                                 │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ REDEMPTION (Phases 5-7)                                           ✅ Done  │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Phase 5: Catalog System                                                    │  │
│  │   • Categories, operators, plans                                           │  │
│  │   • Points override per operator                                           │  │
│  │   • Promotional badges                                                     │  │
│  │                                                                            │  │
│  │ Phase 6: Redemption Core                                                   │  │
│  │   • Points/cash split calculation                                          │  │
│  │   • Order lifecycle management                                             │  │
│  │   • FIFO deduction from buckets                                            │  │
│  │                                                                            │  │
│  │ Phase 7: Notifications                                                     │  │
│  │   • WhatsApp notifications                                                 │  │
│  │   • Template customization                                                 │  │
│  │   • Event enable/disable                                                   │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ INTEGRATION (Phases 8-10)                                         ✅ Done  │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Phase 8: Migration System                                                  │  │
│  │   • Batch migration from legacy rewards                                    │  │
│  │   • Preview, execute, rollback                                             │  │
│  │                                                                            │  │
│  │ Phase 9: Payment Gateway                                                   │  │
│  │   • PayU/Razorpay integration                                              │  │
│  │   • Webhook processing                                                     │  │
│  │                                                                            │  │
│  │ Phase 10: Dashboard & Polish                                               │  │
│  │   • Admin dashboard with metrics                                           │  │
│  │   • Audit logging                                                          │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ BBPS GATEWAY (Phases 11-15)                                       ✅ Done  │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ Phase 11: Abstraction Foundation                                           │  │
│  │   • IBBPSGatewayAdapter interface                                          │  │
│  │   • BBPSGatewayFactory with failover                                       │  │
│  │   • PayU adapter with OAuth 2.0                                            │  │
│  │                                                                            │  │
│  │ Phase 12: Catalog Sync                                                     │  │
│  │   • Provider-agnostic sync service                                         │  │
│  │   • Scheduled sync job                                                     │  │
│  │                                                                            │  │
│  │ Phase 13: Bill Operations                                                  │  │
│  │   • Fetch bill, pay bill, status check                                     │  │
│  │                                                                            │  │
│  │ Phase 14: Prepaid Recharge                                                 │  │
│  │   • Operator detection, plan fetching                                      │  │
│  │   • Execute recharge                                                       │  │
│  │                                                                            │  │
│  │ Phase 15: Razorpay + Production                                            │  │
│  │   • RazorpayBBPSAdapter                                                    │  │
│  │   • Circuit breaker, retry handler                                         │  │
│  │   • Health monitoring, audit logging                                       │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
│  ┌───────────────────────────────────────────────────────────────────────────┐  │
│  │ MIXED REDEMPTION (Phase 16)                                       ✅ Done  │  │
│  ├───────────────────────────────────────────────────────────────────────────┤  │
│  │ • Points + Cash redemption flow                                            │  │
│  │ • Razorpay order creation for cash portion                                 │  │
│  │ • Payment confirmation via webhook                                         │  │
│  │ • Points locking during payment collection                                 │  │
│  │ • Orchestration: cash collection → BBPS payment → points deduction         │  │
│  │ • Refund handling if BBPS fails after cash collected                       │  │
│  │ • Payment timeout job for cleanup                                          │  │
│  └───────────────────────────────────────────────────────────────────────────┘  │
│                                                                                  │
└─────────────────────────────────────────────────────────────────────────────────┘
`,
    },
  ],
};

export function PointsApiHelpPage() {
  const [, setSelectedCategory] = useState<Category>(POINTS_API_CATEGORIES[0]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint>(POINTS_API_CATEGORIES[0].endpoints[0]);
  const [activeTab, setActiveTab] = useState<'request' | 'response' | 'test'>('request');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<'api' | 'integration' | 'architecture'>('api');
  const [selectedGuideSection, setSelectedGuideSection] = useState(MOBILE_INTEGRATION_GUIDE.sections[0].id);
  
  // Test state
  const [testPathParams, setTestPathParams] = useState<Record<string, string>>({});
  const [testQueryParams, setTestQueryParams] = useState<Record<string, string>>({});
  const [testRequestBody, setTestRequestBody] = useState('');
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  const selectEndpoint = (endpoint: Endpoint) => {
    setSelectedEndpoint(endpoint);
    setActiveTab('request');
    setTestResponse(null);
    setTestError(null);
    
    // Initialize params
    const pathParams: Record<string, string> = {};
    endpoint.pathParams?.forEach(p => { pathParams[p.name] = ''; });
    setTestPathParams(pathParams);
    
    const queryParams: Record<string, string> = {};
    endpoint.queryParams?.forEach(p => { queryParams[p.name] = p.default || ''; });
    setTestQueryParams(queryParams);
    
    setTestRequestBody(endpoint.requestBody ? JSON.stringify(endpoint.requestBody, null, 2) : '{}');
  };

  const sendTestRequest = async () => {
    setTestLoading(true);
    setTestError(null);
    setTestResponse(null);
    
    try {
      let url = selectedEndpoint.path;
      Object.entries(testPathParams).forEach(([key, value]) => {
        url = url.replace(`:${key}`, encodeURIComponent(value || 'undefined'));
      });
      
      const queryString = Object.entries(testQueryParams)
        .filter(([_, v]) => v !== '')
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
      if (queryString) url += `?${queryString}`;
      
      const baseUrl = (api as any).baseUrl || '';
      const options: RequestInit = {
        method: selectedEndpoint.method,
        headers: { 'Content-Type': 'application/json', 'x-api-key': (api as any).apiKey || '' },
      };
      
      if (['POST', 'PUT'].includes(selectedEndpoint.method)) {
        options.body = testRequestBody;
      }
      
      const response = await fetch(`${baseUrl}${url}`, options);
      const data = await response.json();
      
      if (!response.ok) {
        setTestError(`HTTP ${response.status}`);
      }
      setTestResponse(data);
    } catch (err: any) {
      setTestError(err.message);
    } finally {
      setTestLoading(false);
    }
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  return (
    <div className="flex h-full min-h-[600px]">
      {/* Sidebar - Collapsible */}
      <div className={`${sidebarCollapsed ? 'w-14' : 'w-72'} bg-white border-r overflow-y-auto flex-shrink-0 transition-all duration-200 relative`}>
        {/* Collapse Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors z-10"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? '»' : '«'}
        </button>
        
        {!sidebarCollapsed && (
          <div className="pt-6 border-b bg-gray-50">
            {/* View Mode Toggle - Vertical */}
            <div className="mb-4">
              <button
                onClick={() => setViewMode('api')}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                  viewMode === 'api' 
                    ? 'border-orange-500 bg-orange-50 text-orange-700' 
                    : 'border-transparent hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span className="text-lg">📖</span>
                <span className="font-medium">API Docs</span>
              </button>
              <button
                onClick={() => {
                  setViewMode('integration');
                  setSelectedGuideSection(MOBILE_INTEGRATION_GUIDE.sections[0].id);
                }}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                  viewMode === 'integration' 
                    ? 'border-orange-500 bg-orange-50 text-orange-700' 
                    : 'border-transparent hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span className="text-lg">📱</span>
                <span className="font-medium">App Guide</span>
              </button>
              <button
                onClick={() => {
                  setViewMode('architecture');
                  setSelectedGuideSection(ARCHITECTURE_CONTENT.sections[0].id);
                }}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                  viewMode === 'architecture' 
                    ? 'border-orange-500 bg-orange-50 text-orange-700' 
                    : 'border-transparent hover:bg-gray-100 text-gray-700'
                }`}
              >
                <span className="text-lg">🏗️</span>
                <span className="font-medium">Architecture</span>
              </button>
            </div>
            
            <div className="px-4 pb-3">
              <h2 className="font-bold">{viewMode === 'api' ? 'API Reference' : viewMode === 'integration' ? 'App Integration' : 'Architecture'}</h2>
              <p className="text-sm text-gray-600">
                {viewMode === 'api' 
                  ? `${POINTS_API_CATEGORIES.reduce((acc, c) => acc + c.endpoints.length, 0)} endpoints`
                  : viewMode === 'integration'
                    ? `${MOBILE_INTEGRATION_GUIDE.sections.length} sections`
                    : `${ARCHITECTURE_CONTENT.sections.length} sections`
                }
              </p>
            </div>
          </div>
        )}
        
        <div className={sidebarCollapsed ? 'pt-10' : ''}>
          {/* API Reference Sidebar */}
          {viewMode === 'api' && POINTS_API_CATEGORIES.map((cat) => (
            <div key={cat.id} className="border-b">
              {sidebarCollapsed ? (
                <div className="py-2 flex justify-center text-lg" title={cat.name}>{cat.icon}</div>
              ) : (
                <>
                  <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase flex items-center gap-2">
                    <span>{cat.icon}</span> {cat.name}
                  </div>
                  {cat.endpoints.map((ep) => (
                    <button
                      key={ep.id}
                      onClick={() => { setSelectedCategory(cat); selectEndpoint(ep); }}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-50 border-l-2 ${selectedEndpoint.id === ep.id ? 'border-linepe-orange bg-orange-50' : 'border-transparent'}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`${METHOD_COLORS[ep.method]} text-white text-xs font-bold px-2 py-0.5 rounded`}>{ep.method}</span>
                        <span className="text-sm font-medium truncate">{ep.title}</span>
                      </div>
                      <code className="text-xs text-gray-500 truncate block">{ep.path}</code>
                    </button>
                  ))}
                </>
              )}
            </div>
          ))}
          
          {/* Integration Guide Sidebar */}
          {viewMode === 'integration' && MOBILE_INTEGRATION_GUIDE.sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setSelectedGuideSection(section.id)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-l-2 transition-colors ${
                selectedGuideSection === section.id ? 'border-linepe-orange bg-orange-50' : 'border-transparent'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{section.icon}</span>
                <span className="text-sm font-medium">{section.title}</span>
              </div>
            </button>
          ))}
          
          {/* Architecture Sidebar */}
          {viewMode === 'architecture' && ARCHITECTURE_CONTENT.sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setSelectedGuideSection(section.id)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-l-2 transition-colors ${
                selectedGuideSection === section.id ? 'border-linepe-orange bg-orange-50' : 'border-transparent'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{section.icon}</span>
                <span className="text-sm font-medium">{section.title}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        
        {/* API Reference View */}
        {viewMode === 'api' && (
          <>
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center gap-3 mb-2">
            <span className={`${METHOD_COLORS[selectedEndpoint.method]} text-white text-sm font-bold px-3 py-1 rounded`}>{selectedEndpoint.method}</span>
            <h1 className="text-2xl font-bold">{selectedEndpoint.title}</h1>
          </div>
          <code className="text-sm bg-gray-100 px-3 py-1 rounded">{selectedEndpoint.path}</code>
          <p className="text-gray-600 mt-3">{selectedEndpoint.description}</p>
          <div className="mt-2 text-sm"><span className="text-gray-500">Auth:</span> <span className="font-medium">{selectedEndpoint.auth}</span></div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b flex">
            {['request', 'response', 'test'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`px-6 py-3 font-semibold capitalize ${activeTab === tab ? 'text-linepe-orange border-b-2 border-linepe-orange' : 'text-gray-600'}`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === 'request' && (
              <div className="space-y-6">
                {selectedEndpoint.pathParams && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Path Parameters</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {selectedEndpoint.pathParams.map((p) => (
                        <div key={p.name} className="flex gap-4">
                          <code className="text-blue-600">{p.name}</code>
                          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">{p.type}</span>
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>
                          <span className="text-gray-600 text-sm">{p.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedEndpoint.queryParams && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Query Parameters</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {selectedEndpoint.queryParams.map((p) => (
                        <div key={p.name} className="flex gap-4 items-center">
                          <code className="text-blue-600">{p.name}</code>
                          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">{p.type}</span>
                          {p.required && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>}
                          {p.default && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">default: {p.default}</span>}
                          <span className="text-gray-600 text-sm">{p.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedEndpoint.requestBody && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Request Body</h3>
                    <div className="relative">
                      <button onClick={() => copyToClipboard(JSON.stringify(selectedEndpoint.requestBody, null, 2))} className="absolute top-3 right-3 text-xs bg-gray-700 text-white px-3 py-1 rounded">Copy</button>
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">{JSON.stringify(selectedEndpoint.requestBody, null, 2)}</pre>
                    </div>
                  </div>
                )}
                
                <div>
                  <h3 className="text-sm font-semibold mb-3">cURL Example</h3>
                  <div className="relative">
                    <button onClick={() => copyToClipboard(selectedEndpoint.curlExample)} className="absolute top-3 right-3 text-xs bg-gray-700 text-white px-3 py-1 rounded">Copy</button>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm whitespace-pre-wrap">{selectedEndpoint.curlExample}</pre>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'response' && (
              <div>
                <h3 className="text-sm font-semibold mb-3">Success Response (200)</h3>
                <div className="relative">
                  <button onClick={() => copyToClipboard(JSON.stringify(selectedEndpoint.responseExample, null, 2))} className="absolute top-3 right-3 text-xs bg-gray-700 text-white px-3 py-1 rounded">Copy</button>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">{JSON.stringify(selectedEndpoint.responseExample, null, 2)}</pre>
                </div>
              </div>
            )}

            {activeTab === 'test' && (
              <div className="space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm text-blue-800"><strong>Live API Testing</strong> - Modify parameters and send a real request.</p>
                </div>

                {selectedEndpoint.pathParams && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Path Parameters</h3>
                    <div className="space-y-3">
                      {selectedEndpoint.pathParams.map((p) => (
                        <div key={p.name}>
                          <label className="text-sm font-medium">{p.name} <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={testPathParams[p.name] || ''}
                            onChange={(e) => setTestPathParams({ ...testPathParams, [p.name]: e.target.value })}
                            placeholder={p.description}
                            className="w-full mt-1 px-3 py-2 border rounded-lg"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedEndpoint.queryParams && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Query Parameters</h3>
                    <div className="space-y-3">
                      {selectedEndpoint.queryParams.map((p) => (
                        <div key={p.name}>
                          <label className="text-sm font-medium">{p.name}</label>
                          <input
                            type="text"
                            value={testQueryParams[p.name] || ''}
                            onChange={(e) => setTestQueryParams({ ...testQueryParams, [p.name]: e.target.value })}
                            placeholder={p.default || p.description}
                            className="w-full mt-1 px-3 py-2 border rounded-lg"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {['POST', 'PUT'].includes(selectedEndpoint.method) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Request Body (JSON)</h3>
                    <textarea
                      value={testRequestBody}
                      onChange={(e) => setTestRequestBody(e.target.value)}
                      rows={8}
                      className="w-full px-3 py-2 font-mono text-sm border rounded-lg"
                    />
                  </div>
                )}

                <button
                  onClick={sendTestRequest}
                  disabled={testLoading}
                  className="px-6 py-2 bg-linepe-orange text-white font-semibold rounded-lg hover:bg-orange-600 disabled:bg-gray-400"
                >
                  {testLoading ? 'Sending...' : 'Send Request'}
                </button>

                {(testResponse || testError) && (
                  <div className="mt-6">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      {testError ? <span className="text-red-600">❌ Error</span> : <span className="text-green-600">✅ Response</span>}
                    </h3>
                    {testError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 mb-3">{testError}</div>}
                    {testResponse && (
                      <pre className={`p-4 rounded-lg overflow-x-auto text-sm ${testError ? 'bg-red-900 text-red-100' : 'bg-gray-900 text-gray-100'}`}>
                        {JSON.stringify(testResponse, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
          </>
        )}
        
        {/* Integration Guide View */}
        {viewMode === 'integration' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">📱</span>
                <div>
                  <h1 className="text-2xl font-bold">{MOBILE_INTEGRATION_GUIDE.title}</h1>
                  <p className="text-gray-600">{MOBILE_INTEGRATION_GUIDE.subtitle}</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Prerequisites:</strong> Retrofit 2.x, Kotlin Coroutines, Jetpack Compose (for UI examples)
                </p>
              </div>
            </div>
            
            {/* Selected Section Content */}
            {MOBILE_INTEGRATION_GUIDE.sections
              .filter(section => section.id === selectedGuideSection)
              .map(section => (
                <div key={section.id} className="space-y-6">
                  {/* Section Header */}
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{section.icon}</span>
                      <h2 className="text-xl font-bold">{section.title}</h2>
                    </div>
                    
                    {/* Steps */}
                    <div className="space-y-8">
                      {section.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="border-l-4 border-orange-400 pl-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-orange-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                              {stepIndex + 1}
                            </span>
                            <h3 className="font-semibold text-lg">{step.title}</h3>
                          </div>
                          <p className="text-gray-600 mb-4">{step.description}</p>
                          
                          {/* Code Block */}
                          <div className="relative">
                            <div className="absolute top-2 right-2 flex gap-2">
                              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">{step.language}</span>
                              <button
                                onClick={() => navigator.clipboard.writeText(step.code)}
                                className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded hover:bg-gray-600"
                              >
                                Copy
                              </button>
                            </div>
                            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                              <code>{step.code}</code>
                            </pre>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            }
            
            {/* Navigation */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                {(() => {
                  const currentIndex = MOBILE_INTEGRATION_GUIDE.sections.findIndex(s => s.id === selectedGuideSection);
                  const prevSection = currentIndex > 0 ? MOBILE_INTEGRATION_GUIDE.sections[currentIndex - 1] : null;
                  const nextSection = currentIndex < MOBILE_INTEGRATION_GUIDE.sections.length - 1 ? MOBILE_INTEGRATION_GUIDE.sections[currentIndex + 1] : null;
                  
                  return (
                    <>
                      {prevSection ? (
                        <button
                          onClick={() => setSelectedGuideSection(prevSection.id)}
                          className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
                        >
                          <span>←</span>
                          <span>{prevSection.icon} {prevSection.title}</span>
                        </button>
                      ) : <div />}
                      
                      {nextSection ? (
                        <button
                          onClick={() => setSelectedGuideSection(nextSection.id)}
                          className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
                        >
                          <span>{nextSection.icon} {nextSection.title}</span>
                          <span>→</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setViewMode('api')}
                          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
                        >
                          <span>View Full API Reference</span>
                          <span>→</span>
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
        
        {/* Architecture View */}
        {viewMode === 'architecture' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">🏗️</span>
                <div>
                  <h1 className="text-2xl font-bold">{ARCHITECTURE_CONTENT.title}</h1>
                  <p className="text-gray-600">{ARCHITECTURE_CONTENT.subtitle}</p>
                </div>
              </div>
              <div className="mt-4 flex gap-4">
                <div className="flex-1 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>✅ Phases 1-16 Complete</strong> — Full points earning, redemption, BBPS integration, and mixed redemption
                  </p>
                </div>
                <div className="flex-1 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>89+ APIs</strong> — Public, Admin, and Webhook endpoints
                  </p>
                </div>
              </div>
            </div>
            
            {/* Selected Section Content */}
            {ARCHITECTURE_CONTENT.sections
              .filter(section => section.id === selectedGuideSection)
              .map(section => (
                <div key={section.id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl">{section.icon}</span>
                    <h2 className="text-xl font-bold">{section.title}</h2>
                  </div>
                  
                  {/* Content as preformatted text (ASCII diagrams) */}
                  <div className="relative">
                    <button
                      onClick={() => navigator.clipboard.writeText(section.content)}
                      className="absolute top-2 right-2 text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded hover:bg-gray-600"
                    >
                      Copy
                    </button>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed font-mono">
                      <code>{section.content}</code>
                    </pre>
                  </div>
                </div>
              ))
            }
            
            {/* Navigation */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                {(() => {
                  const currentIndex = ARCHITECTURE_CONTENT.sections.findIndex(s => s.id === selectedGuideSection);
                  const prevSection = currentIndex > 0 ? ARCHITECTURE_CONTENT.sections[currentIndex - 1] : null;
                  const nextSection = currentIndex < ARCHITECTURE_CONTENT.sections.length - 1 ? ARCHITECTURE_CONTENT.sections[currentIndex + 1] : null;
                  
                  return (
                    <>
                      {prevSection ? (
                        <button
                          onClick={() => setSelectedGuideSection(prevSection.id)}
                          className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
                        >
                          <span>←</span>
                          <span>{prevSection.icon} {prevSection.title}</span>
                        </button>
                      ) : <div />}
                      
                      {nextSection ? (
                        <button
                          onClick={() => setSelectedGuideSection(nextSection.id)}
                          className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
                        >
                          <span>{nextSection.icon} {nextSection.title}</span>
                          <span>→</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setViewMode('api')}
                          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
                        >
                          <span>Explore APIs</span>
                          <span>→</span>
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
